import classNames from 'classnames';
import { connect } from 'react-redux';
import { compose } from 'redux';
import {
    defineMessages,
    FormattedMessage,
    injectIntl,
    intlShape,
} from 'react-intl';
import PropTypes from 'prop-types';
import bindAll from 'lodash.bindall';
import bowser from 'bowser';
import React from 'react';

import VM from 'scratch-vm';
import ScratchBlocks from 'scratch-blocks';
import {
    parseVersion,
    WHO_CODE_TO_DEVICE_FAMILY,
    DEVICE_FAMILY_LABELS,
} from 'scratch-vm/src/util/device-version';

import Box from '../box/box.jsx';
import Button from '../button/button.jsx';
import DeviceRedirectModal from '../device-redirect-modal/device-redirect-modal.jsx';
import CommunityButton from './community-button.jsx';
import ShareButton from './share-button.jsx';
import { ComingSoonTooltip } from '../coming-soon/coming-soon.jsx';
import Divider from '../divider/divider.jsx';
import LanguageSelector from '../../containers/language-selector.jsx';
import SaveStatus from './save-status.jsx';
import SBFileUploader from '../../containers/sb-file-uploader.jsx';
import ProjectWatcher from '../../containers/project-watcher.jsx';
import MenuBarMenu from './menu-bar-menu.jsx';
import { MenuItem, MenuSection } from '../menu/menu.jsx';
import ProjectTitleInput from './project-title-input.jsx';
import AuthorInfo from './author-info.jsx';
import AccountNav from '../../containers/account-nav.jsx';
import LoginDropdown from './login-dropdown.jsx';
import SB3Downloader from '../../containers/sb3-downloader.jsx';
import DeletionRestorer from '../../containers/deletion-restorer.jsx';
import TurboMode from '../../containers/turbo-mode.jsx';
import MenuBarHOC from '../../containers/menu-bar-hoc.jsx';
import inputImg from '../../../static/select bar@2x.png';
import { openTipsLibrary } from '../../reducers/modals';
import { setPlayer } from '../../reducers/mode';
import {
    autoUpdateProject,
    getIsUpdating,
    getIsShowingProject,
    manualUpdateProject,
    requestNewProject,
    remixProject,
    saveProjectAsCopy,
} from '../../reducers/project-state';
import {
    openAccountMenu,
    closeAccountMenu,
    accountMenuOpen,
    openFileMenu,
    closeFileMenu,
    fileMenuOpen,
    openEditMenu,
    closeEditMenu,
    editMenuOpen,
    openLanguageMenu,
    closeLanguageMenu,
    languageMenuOpen,
    openLoginMenu,
    closeLoginMenu,
    loginMenuOpen,
} from '../../reducers/menus';

import collectMetadata from '../../lib/collect-metadata';

import styles from './menu-bar.css';

import helpIcon from '../../lib/assets/icon--tutorials.svg';
import mystuffIcon from './icon--mystuff.png';
import profileIcon from './icon--profile.png';
import remixIcon from './icon--remix.svg';
import dropdownCaret from './dropdown-caret.svg';
import languageIcon from '../language-selector/language-icon.svg';
import aboutIcon from './icon--about.svg';

import scratchLogo from './back@3x.png';
import saveIcon from './save-secondary@3x.png';
import clearProgram from './resetBtn.png';
import uploadBtn from './uploadBtn.png';
import usbOff from './usb-off@3x.png';
import usbOn from './usb-on@3x.png';
import closeBtn from './close@3x.png';

import sharedMessages from '../../lib/shared-messages';
import { resetPortBusy, setPortBusy, setDeviceToast } from '../../reducers/device.js';
import { showMessageAlertWithTimeout } from '../../reducers/alerts';
import { setProjectTitle } from '../../reducers/project-title.js';
import { setCodes, setCurrentMode } from '../../reducers/script.js';

const ariaMessages = defineMessages({
    language: {
        id: 'gui.menuBar.LanguageSelector',
        defaultMessage: 'language selector',
        description: 'accessibility text for the language selection menu',
    },
    tutorials: {
        id: 'gui.menuBar.tutorialsLibrary',
        defaultMessage: 'Tutorials',
        description: 'accessibility text for the tutorials button',
    },
});

const MenuBarItemTooltip = ({
    children,
    className,
    enable,
    id,
    place = 'bottom',
}) => {
    if (enable) {
        return <React.Fragment>{children}</React.Fragment>;
    }
    return (
        <ComingSoonTooltip
            className={classNames(styles.comingSoon, className)}
            place={place}
            tooltipClassName={styles.comingSoonTooltip}
            tooltipId={id}
        >
            {children}
        </ComingSoonTooltip>
    );
};

MenuBarItemTooltip.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    enable: PropTypes.bool,
    id: PropTypes.string,
    place: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
};

const MenuItemTooltip = ({ id, isRtl, children, className }) => (
    <ComingSoonTooltip
        className={classNames(styles.comingSoon, className)}
        isRtl={isRtl}
        place={isRtl ? 'left' : 'right'}
        tooltipClassName={styles.comingSoonTooltip}
        tooltipId={id}
    >
        {children}
    </ComingSoonTooltip>
);

MenuItemTooltip.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    id: PropTypes.string,
    isRtl: PropTypes.bool,
};

const AboutButton = props => (
    <Button
        className={classNames(styles.menuBarItem, styles.hoverable)}
        iconClassName={styles.aboutIcon}
        iconSrc={aboutIcon}
        onClick={props.onClick}
    />
);

AboutButton.propTypes = {
    onClick: PropTypes.func.isRequired,
};
const toStoredDeviceCode = code => (code === 'PC' || code === 'Ace' ? 'Ace' : code);
class MenuBar extends React.Component {
    constructor(props) {
        super(props);
        this.sendDataRef = React.createRef();
        this.sendDataRef.current = {
            sendDataAsPacket: false,
            readVal: '',
            type: '',
        };

        const params = new URLSearchParams(window.location.search);
        this.device = params.get('device') || null;
        this.version = params.get('version') || null;

        const majorVersion = parseVersion(this.version).major;
        this.isAiEnable =
            (this.device === 'peeCee' && majorVersion === 1) ||
            (this.device === 'zing' && majorVersion === 2) ||
            this.device === 'poki';

        // AI lists are fetched lazily, only when the matching block/dropdown is
        // used. aiListDone dedupes per session; aiFetchQueue serialises them.
        this.aiListDone = {};
        this.aiFetchQueue = Promise.resolve();

        bindAll(this, [
            'handleClickNew',
            'handleClickRemix',
            'handleClickSave',
            'handleClickSaveAsCopy',
            'handleClickSeeCommunity',
            'handleClickShare',
            'handleKeyPress',
            'handleLanguageMouseUp',
            'handleRestoreOption',
            'getSaveToComputerHandler',
            'restoreOptionMessage',
            'handleClickUSB',
            'handleFocus',
            'handleBlur',
            'messageHandler',
            'confirmDeviceRedirect',
            'cancelDeviceRedirect',
        ]);
        this.state = {
            usbConnectionStatus: false,
            showSavePopup: false,
            tempProjectTitle: '',
            fileNameError: '',
            pendingDeviceRedirect: null,
        };
        sessionStorage.setItem('usbConnectionStatus', false);
    }

    // Requested by the AI extension's menu getters (via the FETCH_AI_LIST
    // runtime event) only when a face/speech/audio block is actually shown.
    // Serialised so several dropdowns opening at once don't clash on the port.
    // When an AI extension is added/selected, probe whether the matching module
    // version actually answers its firmware-version command.
    handleCheckAiVersion = async protocol => {
        if (!this.isAiEnable) {
            return;
        }
        const label = protocol === 'v2' ? 'AI-1 v2' : 'AI-1 v1';
        if (sessionStorage.getItem('usbConnectionStatus') !== 'true') {
            this.props.setDeviceToast(
                'error',
                `Connect the device to detect ${label}`
            );
            return;
        }
        if (this.props.portBusy && this.props.portBusy.isPortBusy) {
            return;
        }
        const crc = this.props.vm.runtime.Bisoft.crc_create;
        let bytes;
        if (protocol === 'v2') {
            const payload = [0x03, 0xd3, 1]; // READ, FW_VERSION, read length
            const len = payload.length;
            bytes = [
                0xfa,
                0xf0,
                (len >> 8) & 0xff,
                len & 0xff,
                ...payload,
                crc([(len >> 8) & 0xff, len & 0xff, ...payload]),
                0xff,
                0xf9,
            ];
        } else {
            bytes = [0x41, 0x49, 2, 0x3f, crc([2, 0x3f]), 0xff, 0xf9];
        }

        this.props.setPortBusy({
            isPortBusy: true,
            portBusyFor: 'checkAiVersion',
            message: `Detecting ${label}...`,
        });

        const detected = await new Promise(resolve => {
            let settled = false;
            const finish = value => {
                if (settled) return;
                settled = true;
                this.props.worker.removeEventListener('message', onMsg);
                clearTimeout(timer);
                resolve(value);
            };
            const onMsg = e => {
                if (e.data && e.data.type === 'readAnyFormat') {
                    finish(true);
                }
            };
            this.props.worker.addEventListener('message', onMsg);
            const timer = setTimeout(() => finish(false), 3000);
            this.props.worker.postMessage({
                type: 'WriteArrayAiTrainFetch',
                value: bytes,
            });
        });

        this.props.resetPortBusy();
        if (detected) {
            this.props.setDeviceToast('success', `${label} connected`);
        } else {
            this.props.setDeviceToast(
                'error',
                `${label} not detected — check the module connection`
            );
        }
    }

    handleFetchAiList = (which, protocol = 'v1') => {
        this.aiFetchQueue = this.aiFetchQueue
            .then(() => this.runAiListFetch(which, protocol))
            .catch(e => /* eslint-disable */console.log(...oo_oo(`1425842089_310_24_310_47_4`,'ERROR', e)));
        return this.aiFetchQueue;
    }

    // v1 lists ride the A/I protocol; v2 (AI-0) lists ride the 0xFA protocol and
    // have no audio/MP3 list (v2 uses text-to-speech instead).
    aiListConfig = {
        v1: {
            audio: {
                cmd: 0x1c,
                portBusyFor: 'fetchAudioList',
                message: 'Fetching the audio list, please wait...',
                waitTime: 7000,
            },
            face: {
                cmd: 0x1f,
                portBusyFor: 'fetchFaceList',
                message: 'Fetching the face list, please wait...',
                waitTime: 12000,
            },
            speech: {
                cmd: 0x15,
                portBusyFor: 'fetchSpeechList',
                message: 'Fetching the speech list, please wait...',
                waitTime: 7000,
            },
        },
        v2: {
            face: {
                cmd: 0x33,
                portBusyFor: 'fetchFaceList',
                message: 'Fetching the face list, please wait...',
                waitTime: 3000,
            },
            speech: {
                cmd: 0x83,
                portBusyFor: 'fetchSpeechList',
                message: 'Fetching the speech list, please wait...',
                waitTime: 3000,
            },
        },
    }

    buildAiFetchFrame(protocol, cmd) {
        const crc = this.props.vm.runtime.Bisoft.crc_create;
        if (protocol === 'v2') {
            // 0xFA 0xF0 LEN_MSB LEN_LSB TYPE(READ=0x03) CMD <read-len> CRC 0xFF 0xF9
            const payload = [0x03, cmd, 0];
            const len = payload.length;
            return [
                0xfa,
                0xf0,
                (len >> 8) & 0xff,
                len & 0xff,
                ...payload,
                crc([(len >> 8) & 0xff, len & 0xff, ...payload]),
                0xff,
                0xf9,
            ];
        }
        // v1: A I LEN CMD CRC 0xFF 0xF9
        return [0x41, 0x49, 2, cmd, crc([2, cmd]), 0xff, 0xf9];
    }

    runAiListFetch = async(which, protocol) => {
        const perProtocol = this.aiListConfig[protocol];
        const cfg = perProtocol && perProtocol[which];
        if (!cfg || !this.isAiEnable) {
            return;
        }
        if (sessionStorage.getItem('usbConnectionStatus') !== 'true') {
            return;
        }
        const doneKey = `${protocol}:${which}`;
        if (this.aiListDone[doneKey]) {
            return;
        }
        // Another port operation (e.g. a program upload) is running; leave the
        // list unfetched so the next dropdown open retries.
        if (this.props.portBusy && this.props.portBusy.isPortBusy) {
            return;
        }
        await this.fetchLogic({
            bytes: this.buildAiFetchFrame(protocol, cfg.cmd),
            portBusyFor: cfg.portBusyFor,
            message: cfg.message,
            waitTime: cfg.waitTime,
            protocol,
            attempts: protocol === 'v2' ? 5 : 2,
        });
        this.aiListDone[doneKey] = true;
        try {
            await this.props.vm.extensionManager.refreshBlocks();
        } catch (e) {
            /* eslint-disable */console.log(...oo_oo(`1425842089_404_12_404_73_4`,'ERROR refreshing blocks after AI list fetch', e));
        }
    }
    componentDidMount() {
        // check the scratch window is onfocus or not
        window.addEventListener('focus', this.handleFocus);
        window.addEventListener('blur', this.handleBlur);

        document.addEventListener('keydown', this.handleKeyPress);
        this.props.worker.addEventListener('message', this.messageHandler);
        this.props.vm.runtime.on('FETCH_AI_LIST', this.handleFetchAiList);
        this.props.vm.runtime.on('CHECK_AI_VERSION', this.handleCheckAiVersion);
        // if (window.location.search === '?pc1.0') {
        //     document.addEventListener('DOMContentLoaded', function () {
        //         navigator.usb.getDevices().then((devices) => {
        //             return Connect(devices[0])
        //         })
        //     })
        //     navigator.usb.addEventListener('connect', (e) => {
        //         let port = e.device
        //         Connect(port)
        //     })
        //     navigator.usb.addEventListener('disconnect', (e) => {
        //         this.setState({
        //             usbConnectionStatus: false,
        //         })
        //     })
        // } else {
        document.addEventListener('DOMContentLoaded', async() => {
            const filters = [
                { usbVendorId: 0x1a86, usbProductId: 0x7523 },
                { usbVendorId: 0x303a, usbProductId: 0x1001 },
                { usbVendorId: 0x1a86, usbProductId: 0x8040 },
                { usbVendorId: 0xb1b0, usbProductId: 0x8055 },
            ];
            await navigator.serial.getPorts({ filters }).then(port => {
                Connect(port[0]);
            });
        });
        navigator.serial.addEventListener('connect', e => {
            const port = e.target;
            if (JSON.parse(sessionStorage.getItem('isWindowOnFocus'))) {
                Connect(port);
            }
        });
        navigator.serial.addEventListener('disconnect', e => {
            // console.log(e.target)
            // if (e.target) {
            //     e.target.close()
            // }
            this.props.worker.postMessage({ type: 'disconnected' });
            this.setState({
                usbConnectionStatus: false,
            });
            sessionStorage.setItem('usbConnectionStatus', false);
            // Keep connectedDevice/deviceVersion/deviceMajor/deviceMinor so the
            // app still knows the last-connected device (e.g. when the user
            // navigates back after disconnecting). They are overwritten when a
            // new device connects.
            // sessionStorage.removeItem('deviceVersion');
            // sessionStorage.removeItem('connectedDevice');
            // sessionStorage.removeItem('deviceMajor');
            // sessionStorage.removeItem('deviceMinor');
            // Bug #1: do NOT clear the sensor variant or flip hasFourInOne/
            // hasTwoInOne to false on disconnect. Doing so refreshed the palette
            // with the sensor block gone, which orphaned any sensor block already
            // placed in the workspace (e.g. inside an If) — it vanished. The block
            // must remain; the last-known sensor variant is kept so it stays valid,
            // and reconnecting the same sensor simply resumes. The variant is only
            // updated when a new CONF reports a different sensor.
            window.dispatchEvent(new Event('sensorVariantUpdated'));
        });

        const Connect = async port => {
            // if (window.location.search === '?pc1.0') {
            //     try {
            //         let configNumber_ = 1
            //         let interfaceNumber_ = 1
            //         await port.open()
            //         if (port.configuration === null)
            //             await port.selectConfiguration(configNumber_)
            //         await port.claimInterface(interfaceNumber_)
            //         if (port.opened === true) {
            //             sessionStorage.setItem('usbConnectionStatus', true)
            //             this.setState({
            //                 usbConnectionStatus: true,
            //             })
            //         }
            //     } catch (e) {
            //         console.log(e)
            //     }
            // } else {
            try {
                // await port.open({ baudRate: 120000 })

                const getConfirmation = async() => new Promise((resolve, reject) => {
                    this.props.worker.postMessage({
                        type: 'connected',
                    });
                    const tempHandler = e => {
                        if (e.data.type == 'connected' && e.data.value) {
                            this.props.worker.removeEventListener('message', tempHandler);
                            resolve(true);
                        }
                        if (e.data.type == 'connected' && !e.data.value) {
                            this.props.worker.removeEventListener('message', tempHandler);
                            resolve(false);
                        }
                    };
                    this.props.worker.addEventListener('message', tempHandler);
                });
                const confirm = await getConfirmation();
                if (confirm) {
                    sessionStorage.setItem('usbConnectionStatus', true);
                    this.setState({
                        usbConnectionStatus: true,
                    });

                    await this.getDeviceConfig();
                    if (!this.props.currentMode.isMicroPythonMode) {
                        await this.getCustomAction();
                        this.aiListDone = {};
                    }
                }
            } catch (e) {
                /* eslint-disable */console.log(...oo_oo(`1425842089_529_16_529_39_4`,'ERROR', e));
            }
            // }
        };
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyPress);
        this.props.vm.runtime.off('FETCH_AI_LIST', this.handleFetchAiList);
        this.props.vm.runtime.off('CHECK_AI_VERSION', this.handleCheckAiVersion);
        // if (window.location.search === '?pc1.0') {
        //     document.removeEventListener('DOMContentLoaded', function () {
        //         navigator.usb.getDevices().then((devices) => {
        //             return Connect(devices[0])
        //         })
        //     })
        //     navigator.usb.removeEventListener('connect', (e) => {
        //         this.setState({
        //             usbConnectionStatus: true,
        //         })
        //     })
        //     navigator.usb.removeEventListener('disconnect', (e) => {
        //         const port = e.device
        //         this.setState({
        //             usbConnectionStatus: false,
        //         })
        //         port.releaseInterface(1)
        //     })
        // }
        // const Connect = async (port) => {
        //     try {
        //         let configNumber_ = 1
        //         let interfaceNumber_ = 1
        //         await port.open()
        //         if (port.configuration === null)
        //             await port.selectConfiguration(configNumber_)
        //         await port.claimInterface(interfaceNumber_)
        //         if (port.opened === true) {
        //             sessionStorage.setItem('usbConnectionStatus', true)
        //             this.setState({
        //                 usbConnectionStatus: true,
        //             })
        //         }
        //     } catch (e) {
        //         console.log(e)
        //     }
        // }
        window.removeEventListener('focus', this.handleFocus);
        window.removeEventListener('blur', this.handleBlur);
        this.props.worker.removeEventListener('message', this.messageHandler);
    }

    handleFocus() {
        /* eslint-disable */console.log(...oo_oo(`1425842089_582_8_582_35_4`,'Window focus'));
        sessionStorage.setItem('isWindowOnFocus', true);
    }

    handleBlur() {
        /* eslint-disable */console.log(...oo_oo(`1425842089_587_8_587_42_4`,'window out of focus'));
        sessionStorage.setItem('isWindowOnFocus', false);
    }
    async handleClickUSB() {
        if (sessionStorage.getItem('usbConnectionStatus') === 'true') {
            return;
        }
        // if (window.location.search === '?pc1.0') {
        //     if (!'usb' in navigator) {
        //         window.alert('Web USB API is not supported on this browser')
        //         return
        //     }
        //     const filters = [
        //         { vendorId: 0xb1b0, productId: 0x93c3 },
        //         { vendorId: 0x303a, productId: 0x1001 },
        //     ]
        //     navigator.usb
        //         .requestDevice({
        //             filters,
        //         })
        //         .then(async (port) => {
        //             try {
        //                 let configNumber_ = 1
        //                 let interfaceNumber_ = 1
        //                 await port.open()

        //                 if (port.configuration === null)
        //                     await port.selectConfiguration(configNumber_)
        //                 await port.claimInterface(interfaceNumber_)
        //                 this.setState({
        //                     usbConnectionStatus: true,
        //                 })
        //                 sessionStorage.setItem('usbConnectionStatus', true)
        //             } catch (e) {}
        //         })
        // } else {
        let port;
        if ('serial' in navigator) {
            /* eslint-disable */console.log(...oo_oo(`1425842089_625_12_625_46_4`,'webSerial supported'));
            const filters = [
                { usbVendorId: 0x1a86, usbProductId: 0x7523 },
                { usbVendorId: 0x303a, usbProductId: 0x1001 },
                { usbVendorId: 0x1a86, usbProductId: 0x8040 },
                { usbVendorId: 0xb1b0, usbProductId: 0x8055 },
                { usbVendorId: 0x303A },
            ];
            // navigator.serial
            //     .requestPort({ filters })
            //     .then(async (selectedport) => {
            //         port = selectedport
            //         try {
            //             await port.open({ baudRate: 120000 })
            //         } catch (e) {}
            //         this.setState({
            //             usbConnectionStatus: true,
            //         })
            //         sessionStorage.setItem('usbConnectionStatus', true)
            //         if (
            //             [
            //                 '?zing1.0',
            //                 '?zing1.0s3',
            //                 '?hexapod',
            //                 '?hexapod1.0s3',
            //             ].includes(window.location.search)
            //         ) {
            //             this.props.vm.fetchCustomAction(port)
            //         }
            //         // this.props.vm.greenFlag()
            //     })
            port = await navigator.serial.requestPort({ filters });
            const getConfirmation = async() => new Promise((resolve, reject) => {
                this.props.worker.postMessage({
                    type: 'connected',
                });
                const tempHandler = e => {
                    if (e.data.type == 'connected' && e.data.value) {
                        this.props.worker.removeEventListener('message', tempHandler);
                        resolve(true);
                    }
                    if (e.data.type == 'connected' && !e.data.value) {
                        this.props.worker.removeEventListener('message', tempHandler);
                        resolve(false);
                    }
                };
                this.props.worker.addEventListener('message', tempHandler);
            });
            const confirm = await getConfirmation();
            if (confirm) {
                this.setState({
                    usbConnectionStatus: true,
                });
                sessionStorage.setItem('usbConnectionStatus', true);

                await this.getDeviceConfig();
                if (!this.props.currentMode.isMicroPythonMode) {
                    await this.getCustomAction();
                    this.aiListDone = {};
                }
            }
        } else {
            window.alert('Web Serial API is not supported on this browser');
        }
        // }
    }

    async fetchLogic({
        bytes,
        portBusyFor,
        message,
        waitTime = 2000,
        protocol = 'v1',
        attempts = 2,
    }) {
        if (
            // !['?pc1.0', '?zing1.0s3', '?zing3.0'].includes(
            //     window.location.search
            // )
            !this.isAiEnable
        ) {
            return;
        }

        this.props.setPortBusy({
            isPortBusy: true,
            portBusyFor,
            message,
        });

        await new Promise(reslove => setTimeout(reslove, 500));

        const bisoft = this.props.vm.runtime.Bisoft;
        for (let i = 0; i < attempts; i++) {
            if (sessionStorage.getItem('usbConnectionStatus') == 'false') {
                break;
            }
            this.props.worker.postMessage({
                type: 'WriteArrayAiTrainFetch',
                value: bytes,
            });
            this.sendDataRef.current = {
                sendDataAsPacket: false,
                readVal: '',
                type: portBusyFor,
            };
            let replyData = '';
            try {
                replyData = await Promise.race([
                    this.checkOk(),
                    this.timeoutPromise(waitTime), //
                ]);
            } catch (e) {
                /* eslint-disable */console.log(...oo_oo(`1425842089_738_16_738_39_4`,'ERROR', e));
            }

            if (replyData.type === portBusyFor) {
                /* eslint-disable */console.log(
                    ...oo_oo(`1425842089_742_16_750_17_4`,`[AI-FETCH] ${protocol} ${portBusyFor} — ${(replyData.readVal || []).length
                    } item(s):`,
                    (replyData.readVal || []).map(item =>
                        (item && typeof item === 'object' ?
                            `${item.id}:${item.label}` :
                            item)
                    ))
                );
                if (replyData.type === 'fetchAudioList') {
                    bisoft.audioList = replyData.readVal;
                    break;
                }
                if (replyData.type === 'fetchFaceList') {
                    if (protocol === 'v2') bisoft.faceListV2 = replyData.readVal;
                    else bisoft.faceList = replyData.readVal;
                    break;
                }
                if (replyData.type === 'fetchSpeechList') {
                    if (protocol === 'v2') {
                        bisoft.speechListV2 = replyData.readVal;
                    } else {
                        bisoft.speechList = replyData.readVal;
                    }
                    break;
                }
            }
        }
        this.props.resetPortBusy();
    }

    async getCustomAction() {
        if (
            // [
            //     '?zing1.0',
            //     '?zing1.0s3',
            //     '?hexapod',
            //     '?hexapod1.0s3',
            //     '?zing3.0',
            // ].includes(window.location.search)
            ['zing', 'crawle'].includes(this.device) &&
            [1, 2, 3].includes(parseVersion(this.version).major)
        ) {
            this.props.setPortBusy({
                isPortBusy: true,
                portBusyFor: 'fetchCustomAction',
                message: 'Fetching the custom action please wait....',
            });
            await new Promise(reslove => setTimeout(reslove, 500));
            for (let i = 0; i < 7; i++) {
                this.props.worker.postMessage({
                    type: 'WriteArrayFormat1310',
                    value: ['F'.charCodeAt(0), 'R'.charCodeAt(0)],
                });
                this.sendDataRef.current = {
                    sendDataAsPacket: false,
                    readVal: '',
                    type: 'fetchCustomAction',
                };
                let replyData = '';
                try {
                    replyData = await Promise.race([
                        this.checkOk(),
                        this.timeoutPromise(2000), //
                    ]);
                } catch (e) {
                    /* eslint-disable */console.log(...oo_oo(`1425842089_808_20_808_43_4`,'ERROR', e));
                }

                if (replyData.type === 'fetchCustomAction') {
                    await this.props.vm.fetchCustomAction(replyData.readVal);
                    break;
                }
            }
            this.props.resetPortBusy();
        }
    }

    // Adapt sensor blocks (input_fourinone — a single block whose label + port
    // menu switch between the 4-in-1 and 2-in-1 variants) that are already placed
    // in the workspace to the currently-connected sensor. Bug #1: this used to
    // DISPOSE the blocks (deleting them from the workspace / leaving an empty If).
    // Now we KEEP the block and only fix a PORT value that is invalid for the new
    // variant. The 2-in-1 supports only 'Proximity' and 'Light'; the 4-in-1
    // supports all ports, so nothing needs fixing when switching to 4-in-1.
    adaptSensorBlocksToVariant(hasTwoInOne, variantChanged) {
        /* eslint-disable */console.log(...oo_oo(`1425842089_828_8_828_128_4`,'[CONF] adaptSensorBlocksToVariant called | hasTwoInOne:', hasTwoInOne, '| variantChanged:', variantChanged));
        const workspace = ScratchBlocks.getMainWorkspace();
        if (!workspace) {
            console.warn('[CONF] adaptSensorBlocksToVariant — no main workspace found');
            return;
        }
        const twoInOnePorts = ['Proximity', 'Light'];
        const sensorBlocks = workspace
            .getAllBlocks(false)
            .filter(block => block.type && block.type.includes('input_fourinone'));
        sensorBlocks.forEach(block => {
            if (hasTwoInOne) {
                const port = block.getFieldValue('PORT');
                if (!twoInOnePorts.includes(port)) {
                    /* eslint-disable */console.log(...oo_oo(`1425842089_842_20_842_98_4`,'[CONF] adapting sensor block PORT', port, '→ Proximity (2-in-1)'));
                    block.setFieldValue('Proximity', 'PORT');
                }
            }
            // 4-in-1: all ports valid — keep the block and its PORT as-is.
        });
        if (typeof workspace.render === 'function') {
            workspace.render();
        }
        // The sensor block's LABEL ("Four in one" vs "Two in one sensor reads")
        // is fixed at creation, so setFieldValue above updates the PORT but not
        // the label of an already-placed block. When the variant actually changed
        // and such blocks exist, ask the VM to re-emit the workspace so the blocks
        // are rebuilt from the (authoritative) VM model — this re-runs each block's
        // definition and refreshes the dynamic label, while staying VM-synced so
        // code generation keeps working. Deferred a tick so the PORT field change
        // above flushes to the VM before it re-emits.
        if (variantChanged && sensorBlocks.length > 0 &&
            typeof this.props.vm.refreshWorkspace === 'function') {
            setTimeout(() => {
                /* eslint-disable */console.log(...oo_oo(`1425842089_862_16_862_120_4`,'[CONF] refreshWorkspace() — rebuilding placed blocks so labels match the connected sensor'));
                this.props.vm.refreshWorkspace();
            }, 0);
        }
    }
    // Instead of redirecting immediately, this stages the redirect and shows
    // a confirmation dialog (DeviceRedirectModal) — the actual URL rewrite +
    // reload happens in confirmDeviceRedirect() once the user agrees.
    redirectToConnectedDevice(whoCode, deviceVersion) {
        const family = WHO_CODE_TO_DEVICE_FAMILY[whoCode];
        if (!family) {
            console.warn(
                '[WHO] no known device= family for WHO code:',
                whoCode,
                '— leaving URL unchanged'
            );
            return false;
        }

        const currentParams = new URLSearchParams(window.location.search);
        const currentDevice = currentParams.get('device');
        const currentVersion = currentParams.get('version');
        if (currentDevice === family && currentVersion === deviceVersion) {
            // URL already matches the connected device — nothing to do
            return false;
        }

        /* eslint-disable */console.log(
            ...oo_oo(`1425842089_889_8_897_9_4`,'[WHO] connected device differs from the URL — asking user to confirm redirect:',
            family,
            deviceVersion,
            '(was',
            currentDevice,
            currentVersion,
            ')')
        );
        this.setState({
            pendingDeviceRedirect: {
                currentDevice,
                currentVersion,
                targetFamily: family,
                targetVersion: deviceVersion,
            },
        });
        return true;
    }

    confirmDeviceRedirect() {
        const pending = this.state.pendingDeviceRedirect;
        if (!pending) return;
        /* eslint-disable */console.log(
            ...oo_oo(`1425842089_912_8_916_9_4`,'[WHO] user confirmed device redirect — reloading as',
            pending.targetFamily,
            pending.targetVersion)
        );
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.set('device', pending.targetFamily);
        currentParams.set('version', pending.targetVersion);
        const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
        window.history.replaceState({}, '', newUrl);
        // The user already confirmed via our own dialog — suppress the
        // browser's native "leave page?" prompt (set by project-saver-hoc.jsx
        // to warn about unsaved project changes) for this one reload.
        window.onbeforeunload = null;
        location.reload();
    }

    cancelDeviceRedirect() {
        /* eslint-disable */console.log(
            ...oo_oo(`1425842089_930_8_932_9_4`,'[WHO] user cancelled device redirect — staying on current device (forcing disconnect)')
        );
        this.setState({
            pendingDeviceRedirect: null,
            usbConnectionStatus: false
        });
        sessionStorage.setItem('usbConnectionStatus', false);
        if (this.props.worker) {
            this.props.worker.postMessage({ type: 'disconnected' });
        }
    }

    async getDeviceConfig() {
        /* eslint-disable */console.log(...oo_oo(`1425842089_944_8_944_69_4`,'[getDeviceConfig] starting WHO + CONF sequence'));
        this.props.setPortBusy({
            isPortBusy: true,
            portBusyFor: 'getDeviceConfig',
            message: 'Detecting device, please wait....',
        });
        try {
            await this._getDeviceConfig();
        } finally {
            this.props.resetPortBusy();
        }
    }

    async _getDeviceConfig() {
        let whoSuccess = false;
        let connectedDevice = null;
        for (let i = 0; i < 5; i++) {
            /* eslint-disable */console.log(...oo_oo(`1425842089_961_12_961_51_4`,`[WHO] attempt ${i + 1}/5`));
            this.sendDataRef.current = {
                sendDataAsPacket: false,
                readVal: '',
                type: 'whoQuery',
            };
            this.props.worker.postMessage({ type: 'WriteWho' });
            let whoReply = '';
            try {
                whoReply = await Promise.race([
                    this.checkOk(),
                    this.timeoutPromise(2000),
                ]);
            } catch (e) {
                console.warn('[WHO] attempt', i + 1, 'timed out');
                continue;
            }
            if (whoReply.type === 'whoQuery') {
                // Response format: "PC1.1.11.0-dev.20260615-135214"
                // device = first 2 uppercase letters, version = next semver digits
                const versionStr = whoReply.readVal;
                const deviceMatch = versionStr.match(/^([A-Z]{2})(\d+\.\d+\.\d+)/);
                if (deviceMatch) {
                    connectedDevice = deviceMatch[1];
                    const deviceVersion = deviceMatch[2];
                    const [major, minor, patch] = deviceVersion.split('.');
                    sessionStorage.setItem('connectedDevice', toStoredDeviceCode(connectedDevice));
                    sessionStorage.setItem('deviceVersion', deviceVersion);
                    sessionStorage.setItem('deviceMajor', major);
                    sessionStorage.setItem('deviceMinor', minor);
                    sessionStorage.setItem('devicePatch', patch);
                    /* eslint-disable */console.log(...oo_oo(`1425842089_992_20_992_166_4`,'[WHO] connectedDevice:', connectedDevice, '| deviceVersion:', deviceVersion, '| major:', major, '| minor:', minor, '| patch:', patch));
                    if (this.redirectToConnectedDevice(connectedDevice, deviceVersion)) {
                        return;
                    }
                } else {
                    console.warn('[WHO] could not parse version string:', versionStr);
                }
                whoSuccess = true;
                break;
            }
        }
        if (!whoSuccess) console.warn('[WHO] all attempts failed — device did not respond to WHO');

        if (['RB', 'TK', 'PK', 'MK'].includes(connectedDevice)) {
            /* eslint-disable */console.log(...oo_oo(`1425842089_1006_12_1006_84_4`,`[CONF] skipped — ${connectedDevice} does not support CONF`));
            /* eslint-disable */console.log(...oo_oo(`1425842089_1007_12_1007_53_4`,'[getDeviceConfig] complete'));
            return;
        }
        // 1.0.11 is the first firmware that answers CONF.
        const MIN_CONF_VERSION = { major: 1, minor: 0, patch: 11 };
        const deviceVersionStr = sessionStorage.getItem('deviceVersion') || '0.0.0';
        const { major, minor, patch } = parseVersion(deviceVersionStr);
        const supportsConf =
            major > MIN_CONF_VERSION.major ||
            (major === MIN_CONF_VERSION.major &&
                (minor > MIN_CONF_VERSION.minor ||
                    (minor === MIN_CONF_VERSION.minor &&
                        patch >= MIN_CONF_VERSION.patch)));
        if (!supportsConf) {
            /* eslint-disable */console.log(...oo_oo(`1425842089_1021_12_1021_108_4`,`[CONF] skipped — firmware ${deviceVersionStr} is below 1.0.11, CONF not supported`));
            /* eslint-disable */console.log(...oo_oo(`1425842089_1022_12_1022_53_4`,'[getDeviceConfig] complete'));
            return;
        }

        let confSuccess = false;
        for (let i = 0; i < 5; i++) {
            /* eslint-disable */console.log(...oo_oo(`1425842089_1028_12_1028_76_4`,`[CONF] attempt ${i + 1}/5 — sending [67,79,78,70]`));
            this.sendDataRef.current = {
                sendDataAsPacket: false,
                readVal: '',
                type: 'confQuery',
            };
            this.props.worker.postMessage({
                type: 'WriteArrayFormat1310',
                value: [67, 79, 78, 70], // 'C','O','N','F'
            });
            let confReply = '';
            try {
                confReply = await Promise.race([
                    this.checkOk(),
                    this.timeoutPromise(2000),
                ]);
            } catch (e) {
                console.warn('[CONF] attempt', i + 1, 'timed out');
                continue;
            }
            if (confReply.type === 'confQuery') {
                // Packet format: CF<sensorByte>ER
                // arrayData = [67('C'), 70('F'), sensorByte, 69('E'), 82('R')]
                // sensorByte: 1 = 4-in-1, 2 = 2-in-1
                const arrayData = confReply.readVal;
                const sensorByte = arrayData[2];
                /* eslint-disable */console.log(...oo_oo(`1425842089_1054_16_1054_63_4`,'[CONF] raw arrayData:', arrayData));
                /* eslint-disable */console.log(...oo_oo(`1425842089_1055_16_1055_96_4`,'[CONF] sensorByte (index[2]):', sensorByte, '— 1=4-in-1, 2=2-in-1'));
                const hasFourInOne = sensorByte === 1;
                const hasTwoInOne = sensorByte === 2;
                // Detect a sensor-variant CHANGE (e.g. swapping a 4-in-1 PeeCee for
                // a 2-in-1, or vice-versa) so we can notify the user. sensorVariant
                // is no longer cleared on disconnect, so this survives replugs.
                const prevVariant = sessionStorage.getItem('sensorVariant');
                const variantChanged =
                    prevVariant !== null && Number(prevVariant) !== sensorByte;
                this.props.vm.runtime.Bisoft.hasFourInOne = hasFourInOne;
                this.props.vm.runtime.Bisoft.hasTwoInOne = hasTwoInOne;
                sessionStorage.setItem('sensorVariant', sensorByte);
                /* eslint-disable */console.log(...oo_oo(`1425842089_1067_16_1067_96_4`,'[CONF] hasFourInOne:', hasFourInOne, '| hasTwoInOne:', hasTwoInOne));
                /* eslint-disable */console.log(...oo_oo(`1425842089_1068_16_1068_105_4`,'[CONF] calling extensionManager.refreshBlocks() to update block palette...'));
                try {
                    await this.props.vm.extensionManager.refreshBlocks();
                    /* eslint-disable */console.log(...oo_oo(`1425842089_1071_20_1071_80_4`,'[CONF] refreshBlocks() done — palette updated'));
                } catch (e) {
                    /* eslint-disable */console.error(...oo_tx(`1425842089_1073_20_1073_70_11`,'[CONF] refreshBlocks() failed:', e));
                }
                // Keep any sensor block already placed in the workspace and adapt
                // it to the connected variant (fix an invalid PORT), instead of
                // deleting it. Notify the user when the variant actually changed.
                this.adaptSensorBlocksToVariant(hasTwoInOne, variantChanged);
                if (variantChanged) {
                    const msg = hasTwoInOne ?
                        'PeeCee with a 2-in-1 sensor connected — your sensor blocks were updated to the 2-in-1 sensor.' :
                        'PeeCee with a 4-in-1 sensor connected — your sensor blocks were updated to the 4-in-1 sensor.';
                    this.props.showDeviceAlert(msg, 'info', 'sensorVariant');
                }
                window.dispatchEvent(new Event('sensorVariantUpdated'));
                confSuccess = true;
                break;
            }
        }
        if (!confSuccess) console.warn('[CONF] all attempts failed — device did not respond to CONF');

        /* eslint-disable */console.log(...oo_oo(`1425842089_1092_8_1092_49_4`,'[getDeviceConfig] complete'));
    }
    handleClickNew() {
        // if the project is dirty, and user owns the project, we will autosave.
        // but if they are not logged in and can't save, user should consider
        // downloading or logging in first.
        // Note that if user is logged in and editing someone else's project,
        // they'll lose their work.
        const readyToReplaceProject = this.props.confirmReadyToReplaceProject(
            this.props.intl.formatMessage(sharedMessages.replaceProjectWarning)
        );
        this.props.onRequestCloseFile();
        if (readyToReplaceProject) {
            this.props.onClickNew(this.props.canSave && this.props.canCreateNew);
        }
        this.props.onRequestCloseFile();
        this.props.setCodes('');
        sessionStorage.setItem('C_code', JSON.stringify([]));
    }
    handleClickRemix() {
        this.props.onClickRemix();
        this.props.onRequestCloseFile();
    }
    handleClickSave() {
        this.props.onClickSave();
        this.props.onRequestCloseFile();
    }
    handleClickSaveAsCopy() {
        this.props.onClickSaveAsCopy();
        this.props.onRequestCloseFile();
    }
    handleClickSeeCommunity(waitForUpdate) {
        if (this.props.shouldSaveBeforeTransition()) {
            this.props.autoUpdateProject(); // save before transitioning to project page
            waitForUpdate(true); // queue the transition to project page
        } else {
            waitForUpdate(false); // immediately transition to project page
        }
    }
    handleClickShare(waitForUpdate) {
        if (!this.props.isShared) {
            if (this.props.canShare) {
                // save before transitioning to project page
                this.props.onShare();
            }
            if (this.props.canSave) {
                // save before transitioning to project page
                this.props.autoUpdateProject();
                waitForUpdate(true); // queue the transition to project page
            } else {
                waitForUpdate(false); // immediately transition to project page
            }
        }
    }
    handleRestoreOption(restoreFun) {
        return () => {
            restoreFun();
            this.props.onRequestCloseEdit();
        };
    }
    handleKeyPress(event) {
        const modifier = bowser.mac ? event.metaKey : event.ctrlKey;
        if (modifier && event.key === 's') {
            this.props.onClickSave();
            event.preventDefault();
        }
    }
    getSaveToComputerHandler(downloadProjectCallback) {
        return () => {
            this.props.onRequestCloseFile();
            downloadProjectCallback();
            if (this.props.onProjectTelemetryEvent) {
                const metadata = collectMetadata(
                    this.props.vm,
                    this.props.projectTitle,
                    this.props.locale
                );
                this.props.onProjectTelemetryEvent('projectDidSave', metadata);
            }
        };
    }
    handleLanguageMouseUp(e) {
        if (!this.props.languageMenuOpen) {
            this.props.onClickLanguage(e);
        }
    }
    restoreOptionMessage(deletedItem) {
        switch (deletedItem) {
        case 'Sprite':
            return (
                <FormattedMessage
                    defaultMessage="Restore Sprite"
                    description="Menu bar item for restoring the last deleted sprite."
                    id="gui.menuBar.restoreSprite"
                />
            );
        case 'Sound':
            return (
                <FormattedMessage
                    defaultMessage="Restore Sound"
                    description="Menu bar item for restoring the last deleted sound."
                    id="gui.menuBar.restoreSound"
                />
            );
        case 'Costume':
            return (
                <FormattedMessage
                    defaultMessage="Restore Costume"
                    description="Menu bar item for restoring the last deleted costume."
                    id="gui.menuBar.restoreCostume"
                />
            );
        default: {
            return (
                <FormattedMessage
                    defaultMessage="Restore"
                    description="Menu bar item for restoring the last deleted item in its disabled state." /* eslint-disable-line max-len */
                    id="gui.menuBar.restore"
                />
            );
        }
        }
    }

    timeoutPromise = ms => new Promise((resolve, reject) => {
        setTimeout(() => {
            /* eslint-disable */console.log(...oo_oo(`1425842089_1218_12_1218_46_4`,'HHHHHHHHHH Time Out'));
            reject(new Error('Timeout exceeded'));
        }, ms);
    })

    checkOk = async() => new Promise(async(resolve, reject) => {
        try {
            while (true) {
                const sendData_read = this.sendDataRef.current;
                if (sendData_read) {
                    if (sendData_read.sendDataAsPacket) {
                        return resolve({
                            readVal: sendData_read.readVal,
                            type: sendData_read.type,
                        });
                    }
                }

                await new Promise(reslove => {
                    setTimeout(reslove, 100);
                });
            }
        } catch (e) {
            /* eslint-disable */console.log(...oo_oo(`1425842089_1241_12_1241_26_4`,e));
        }
    })

    // Parse a v2 (AI-0) list reply into [{id, label}]. Frame layout:
    //   250,15, LEN_MSB, LEN_LSB, 04, cmd, <data...>, crc, 255,245
    // The data is a run of <id><ascii text> entries (no delimiters), ending with
    // a '>' (62) marker and FF padding, and may start with a wrap-around tail.
    parseV2List = (value, mood) => {
        const bytes = value.split(',').map(Number);
        // Drop the 6-byte header (250,15,LEN,LEN,type,cmd) and trailing bytes.
        let data = bytes.slice(6, bytes.length - 3);

        // A complete reply is CR-LF (13,10) delimited: <id>\r\n<text>\r\n...
        // (same shape as v1). Fragments may arrive delimiter-less.
        let fields;
        if (data.includes(13)) {
            fields = [];
            let cur = [];
            for (let k = 0; k < data.length; k += 1) {
                if (data[k] === 13 && data[k + 1] === 10) {
                    fields.push(cur);
                    cur = [];
                    k += 1;
                } else {
                    cur.push(data[k]);
                }
            }
            if (cur.length) {
                fields.push(cur);
            }
            // A leading (or stray) CR-LF produces an empty field; drop empties
            // so the <id>,<text> pairing stays aligned.
            fields = fields.filter(f => f.length > 0);
        } else {
            // Delimiter-less: <id><ascii text>, ids are control bytes (< 32);
            // the wakeword (101) is split off the tail below.
            while (
                data.length &&
                (data[data.length - 1] === 255 || data[data.length - 1] === 62)
            ) {
                data.pop();
            }
            const firstGt = data.indexOf(62);
            if (firstGt !== -1) {
                data = data.slice(firstGt + 1);
            }
            fields = [];
            let k = 0;
            while (k < data.length) {
                fields.push([data[k]]);
                k += 1;
                const t = [];
                while (k < data.length && data[k] >= 32) {
                    t.push(data[k]);
                    k += 1;
                }
                fields.push(t);
                if (
                    mood === 'speech' &&
                    t.length &&
                    k >= data.length
                ) {
                    for (let j = 0; j < t.length - 1; j += 1) {
                        if (t[j] === 101 && t[j + 1] >= 65 && t[j + 1] <= 90) {
                            fields[fields.length - 1] = t.slice(0, j);
                            fields.push([101], t.slice(j + 1));
                            break;
                        }
                    }
                }
            }
        }

        // Skip wrap-around / version noise: start after the last field with '>' (62).
        let start = 0;
        for (let k = 0; k < fields.length; k += 1) {
            if (fields[k].includes(62)) {
                start = k + 1;
            }
        }
        fields = fields.slice(start);

        const entries = [];
        for (let k = 0; k + 1 < fields.length; k += 2) {
            const idBytes = fields[k];
            const textBytes = fields[k + 1].filter(b => b >= 32);
            if (!idBytes.length || !textBytes.length) {
                continue;
            }
            const entry = {
                id: String(idBytes[0]),
                label: String.fromCharCode(...textBytes),
            };
            if (mood === 'speech' && idBytes[0] === 101) {
                entries.unshift(entry);
            } else {
                entries.push(entry);
            }
        }
        if (mood === 'face') {
            entries.unshift({ label: 'Face', id: '0' });
        }
        return entries;
    }

    // read data from serial worker
    messageHandler = e => {
        /* eslint-disable */console.log(...oo_oo(`1425842089_1349_8_1349_27_4`,e.data));
        if (e.data.type === 'whoRead' && this.sendDataRef.current?.type === 'whoQuery') {
            /* eslint-disable */console.log(...oo_oo(`1425842089_1351_12_1351_75_4`,'[messageHandler] whoRead received:', e.data.value));
            this.sendDataRef.current = {
                sendDataAsPacket: true,
                readVal: e.data.value.trim(),
                type: 'whoQuery',
            };
            return;
        }
        if (e.data.type === 'readFormat1310') {
            if (this.sendDataRef.current?.type === 'confQuery') {
                const arrayData = e.data.value.split(',').map(Number);
                /* eslint-disable */console.log(...oo_oo(`1425842089_1362_16_1362_107_4`,'[messageHandler] confQuery readFormat1310 received, raw value:', e.data.value));
                this.sendDataRef.current = {
                    sendDataAsPacket: true,
                    readVal: arrayData,
                    type: 'confQuery',
                };
                return;
            }
            const regexEqu = /70,82,([^]*?),10,69,78,68/;
            const match = e.data.value.match(regexEqu);
            if (match) {
                const matchArr = match[1].split(',');
                const extractStringData = String.fromCharCode(...matchArr);

                const fileNameArr = extractStringData.split(/[\n]+/);
                // in the fileNameArr remove the length of actions
                fileNameArr.shift();

                this.sendDataRef.current = {
                    sendDataAsPacket: true,
                    readVal: fileNameArr,
                    type: 'fetchCustomAction',
                };
            }
        }
        // ['fetchFaceList', 'fetchAudioList','fetchSpeechList'].includes(this.sendDataRef.current.type)
        const fetchValue = e.data?.value;

        // v2 (AI-0) list replies start with 250,15 (0xFA 0x0F) and use a
        // different, delimiter-less layout than v1.
        if (
            e.data.type === 'readAnyFormat' &&
            typeof fetchValue === 'string' &&
            fetchValue.startsWith('250,15') &&
            (this.sendDataRef.current.type === 'fetchFaceList' ||
                this.sendDataRef.current.type === 'fetchSpeechList')
        ) {
            const mood =
                this.sendDataRef.current.type === 'fetchSpeechList' ?
                    'speech' :
                    'face';
            // Frame is 250,15,LEN,LEN,type,cmd,... — only accept the reply whose
            // cmd matches this list (0x83 speech / 0x33 face); ignore a stray
            // version (0xD3) or other frame so checkOk keeps waiting.
            const cmd = fetchValue.split(',').map(Number)[5];
            const expected = mood === 'speech' ? 0x83 : 0x33;
            if (cmd !== expected) {
                return;
            }
            this.sendDataRef.current = {
                ...this.sendDataRef.current,
                sendDataAsPacket: true,
                readVal: this.parseV2List(fetchValue, mood),
            };
            return;
        }
        const regexPatterns = {
            default: /(?<=\d+,\d+,28,)(.*?)(?=,\d+,255,245)/,
            fetchRegexEqu: num =>
                new RegExp(
                    `^65,76,\\d+,\\d+,${num},13,10,((?:\\d+,)*?)13,10,\\d+,255,245$`
                ),
        };

        // console.log('HHHHHHHHHH', fetchMatch)

        const processMatch = (match, mood) => {
            if (match) {
                const matchArr = match[1].slice(0, -1).split(',13,10,');

                const resultArr = [];

                if (mood === 'face') {
                    resultArr.push({
                        label: 'Face',
                        id: '0',
                    });
                }
                for (let i = 0; i < matchArr.length - 1; i = i + 2) {
                    const labelArr = matchArr[i + 1].split(',');

                    if (matchArr[i] === '101' && mood === 'speech') {
                        resultArr.unshift({
                            label: String.fromCharCode(...labelArr),
                            id: matchArr[i],
                        });
                    } else {
                        resultArr.push({
                            label: String.fromCharCode(...labelArr),
                            id: matchArr[i],
                        });
                    }
                }

                this.sendDataRef.current = {
                    ...this.sendDataRef.current,
                    sendDataAsPacket: true,
                    readVal: resultArr,
                };

                // sendStopTrainBytes()
            }
        };

        if (
            e.data.type === 'readAnyFormat' &&
            this.sendDataRef.current.type === 'fetchFaceList'
        ) {
            const fetchMatch = fetchValue.match(regexPatterns.fetchRegexEqu(31));
            processMatch(fetchMatch, 'face');
        } else if (
            e.data.type === 'readAnyFormat' &&
            this.sendDataRef.current.type === 'fetchSpeechList'
        ) {
            const fetchSpeechMatch = fetchValue.match(
                regexPatterns.fetchRegexEqu(21)
            );
            processMatch(fetchSpeechMatch, 'speech');
        } else if (
            e.data.type === 'readAnyFormat' &&
            this.sendDataRef.current.type === 'fetchAudioList'
        ) {
            const match = fetchValue.match(regexPatterns.default);
            // 13,10,65,76,0,0,28,84,255,245,13,10
            // if audio file not present check the regex
            const audioNotPresent = fetchValue.match(
                /13,10,65,76,0,0,28,84,255,245,13,10/
            );

            if (match) {
                const matchArr = match[0].split(',');

                const stringData = String.fromCharCode(...matchArr);
                const stringArr = stringData.split('\n');

                // fetchAudioListRef.current = ['Select', ...stringArr]
                // sendDataAsPacket.current = false
                this.sendDataRef.current = {
                    ...this.sendDataRef.current,
                    sendDataAsPacket: true,
                    readVal: [...stringArr],
                };
            } else if (fetchValue === '65,76,0,0,28,84,255,245') {
                this.sendDataRef.current = {
                    ...this.sendDataRef.current,
                    sendDataAsPacket: true,
                    readVal: [],
                };
            }
        }
    }
    validateFileName = name => {
        if (!name.trim()) {
            return 'File name is required';
        }

        if (name.length > 35) {
            return 'File name must be 35 characters or less';
        }

        const validPattern = /^[a-zA-Z0-9 _-]+$/;

        if (!validPattern.test(name)) {
            return 'Special characters are not allowed';
        }

        return '';
    }

    onConfirmSave = downloadProjectCallback => {
        const name = this.state.tempProjectTitle.trim();
        const error = this.validateFileName(name);
        if (error) {
            this.setState({ fileNameError: error });
            return;
        }

        if (!name) return;

        // Update Redux projectTitle
        this.props.onSetProjectTitle(name);

        // Close popup
        this.setState({ showSavePopup: false }, () => {
            // Trigger actual download
            this.getSaveToComputerHandler(downloadProjectCallback)();
        });
    }

    renderSavePopup = downloadProjectCallback => (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                {/* <h3 className={styles.title}>Save Project</h3> */}
                <img
                    className={styles.closeBtn}
                    src={closeBtn}
                    alt="close button"
                    onClick={() => this.setState({ showSavePopup: false })}
                />
                <input
                    type="text"
                    placeholder="Enter the file name"
                    value={this.state.tempProjectTitle}
                    onChange={e =>
                        this.setState({
                            tempProjectTitle: e.target.value,
                            fileNameError: '',
                        })
                    }
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            this.onConfirmSave(downloadProjectCallback);
                        }
                    }}
                    className={styles.input}
                />
                {this.state.fileNameError && (
                    <div className={styles.errorText}>
                        {this.state.fileNameError}
                    </div>
                )}

                <button
                    onClick={() =>
                        this.onConfirmSave(downloadProjectCallback)
                    }
                    className={styles.saveBtn}
                >
                    Save
                </button>
            </div>
        </div>
    )

    toggleSwipeBtn = e => {
        this.props.setCurrentMode({
            ...this.props.currentMode,
            isCodingMode: !this.props.currentMode.isCodingMode,
        });
    }

    render() {
        const saveNowMessage = (
            <FormattedMessage
                defaultMessage="Save now"
                description="Menu bar item for saving now"
                id="gui.menuBar.saveNow"
            />
        );
        const createCopyMessage = (
            <FormattedMessage
                defaultMessage="Save as a copy"
                description="Menu bar item for saving as a copy"
                id="gui.menuBar.saveAsCopy"
            />
        );
        const remixMessage = (
            <FormattedMessage
                defaultMessage="Remix"
                description="Menu bar item for remixing"
                id="gui.menuBar.remix"
            />
        );
        const newProjectMessage = (
            <FormattedMessage
                defaultMessage="New"
                description="Menu bar item for creating a new project"
                id="gui.menuBar.new"
            />
        );
        const remixButton = (
            <Button
                className={classNames(styles.menuBarButton, styles.remixButton)}
                iconClassName={styles.remixButtonIcon}
                iconSrc={remixIcon}
                onClick={this.handleClickRemix}
            >
                {remixMessage}
            </Button>
        );
        // Show the About button only if we have a handler for it (like in the desktop app)
        const aboutButton = this.props.onClickAbout ? (
            <AboutButton onClick={this.props.onClickAbout} />
        ) : null;
        return (
            <React.Fragment>
                <Box className={classNames(this.props.className, styles.menuBar)}>
                    <div className={styles.mainMenu}>
                        <div className={styles.titleCode}>
                            <span>Code</span>
                            <img
                                src={inputImg}
                                alt="code"
                            />
                        </div>
                        <div className={styles.fileGroup}>
                            <div className={classNames(styles.menuBarItem)}>
                                <img
                                    alt="Scratch"
                                    className={classNames(styles.scratchLogo, {
                                        [styles.clickable]:
                                            typeof this.props.onClickLogo !==
                                            'undefined',
                                    })}
                                    draggable={false}
                                    src={this.props.logo}
                                    onClick={this.props.onClickLogo}
                                />
                            </div>

                            {this.props.canManageFiles && (
                                <>
                                    {/* <div
                                    className={classNames(
                                        styles.menuBarItem,
                                        styles.hoverable,
                                        {
                                            [styles.active]:
                                                this.props.fileMenuOpen,
                                        }
                                    )}
                                    onMouseUp={this.props.onClickFile}
                                >
                                    <FormattedMessage
                                        defaultMessage="File"
                                        description="Text for file dropdown menu"
                                        id="gui.menuBar.file"
                                    />
                                    <MenuBarMenu
                                        className={classNames(
                                            styles.menuBarMenu
                                        )}
                                        open={this.props.fileMenuOpen}
                                        place={
                                            this.props.isRtl ? "left" : "right"
                                        }
                                        onRequestClose={
                                            this.props.onRequestCloseFile
                                        }
                                    >
                                        <MenuSection>
                                            <MenuItem
                                                isRtl={this.props.isRtl}
                                                onClick={this.handleClickNew}
                                            >
                                                {newProjectMessage}
                                            </MenuItem>
                                        </MenuSection>
                                        {(this.props.canSave ||
                                            this.props.canCreateCopy ||
                                            this.props.canRemix) && (
                                            <MenuSection>
                                                {this.props.canSave && (
                                                    <MenuItem
                                                        onClick={
                                                            this.handleClickSave
                                                        }
                                                    >
                                                        {saveNowMessage}
                                                    </MenuItem>
                                                )}
                                                {this.props.canCreateCopy && (
                                                    <MenuItem
                                                        onClick={
                                                            this
                                                                .handleClickSaveAsCopy
                                                        }
                                                    >
                                                        {createCopyMessage}
                                                    </MenuItem>
                                                )}
                                                {this.props.canRemix && (
                                                    <MenuItem
                                                        onClick={
                                                            this
                                                                .handleClickRemix
                                                        }
                                                    >
                                                        {remixMessage}
                                                    </MenuItem>
                                                )}
                                            </MenuSection>
                                        )}
                                        <MenuSection>
                                            <SBFileUploader
                                                canSave={this.props.canSave}
                                                userOwnsProject={
                                                    this.props.userOwnsProject
                                                }
                                            >
                                                {(
                                                    className,
                                                    renderFileInput,
                                                    handleLoadProject
                                                ) => (
                                                    <MenuItem
                                                        className={className}
                                                        onClick={
                                                            handleLoadProject
                                                        }
                                                    >
                                                        {/* eslint-disable max-len */}
                                    {/* {this.props.intl.formatMessage(
                                                            sharedMessages.loadFromComputerTitle
                                                        )}
                                                        {/* eslint-enable max-len */}
                                    {/* {renderFileInput()}
                                                    </MenuItem>
                                                )}
                                            </SBFileUploader>
                                            <SB3Downloader>
                                                {(
                                                    className,
                                                    downloadProjectCallback
                                                ) => (
                                                    <MenuItem
                                                        className={className}
                                                        onClick={this.getSaveToComputerHandler(
                                                            downloadProjectCallback
                                                        )}
                                                    >
                                                        <FormattedMessage
                                                            defaultMessage="Save to your computer"
                                                            description="Menu bar item for downloading a project to your computer" // eslint-disable-line max-len
                                                            id="gui.menuBar.downloadToComputer"
                                                        />
                                                    </MenuItem>
                                                )}
                                            </SB3Downloader>
                                        </MenuSection>
                                    </MenuBarMenu>
                                </div> */}
                                </>
                            )}
                        </div>
                        <div
                            style={{
                                height: '90%',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            {/* swaipe button for code mode or  byte mode */}
                            {/* <div
                            className={`${styles.swipe_container} ${
                                this.props.currentMode.isCodingMode
                                    ? styles.true
                                    : ''
                            }`}
                            onClick={(e) => this.toggleSwipeBtn(e)}
                        >
                            <div className={styles.swipe_handle}></div>
                            <div className={styles.left_text}>Real Time</div>
                            <div className={styles.right_text}>Update</div>
                        </div> */}

                            <div
                                style={{
                                    position: 'relative',
                                    marginLeft: 'auto',
                                    marginRight: '0px',
                                    marginTop: '5px',
                                    height: '6vh',
                                    marginLeft: '22px',
                                }}
                            >
                                <img
                                    className={styles.clearImage}
                                    src={clearProgram}
                                    style={{ height: '100%', cursor: 'pointer' }}
                                    onClick={this.handleClickNew}
                                />
                                <div
                                    className={`${styles.headerTooltip} ${styles.clearTooltip}`}
                                >
                                    Clear
                                </div>
                            </div>
                            <div
                                style={{
                                    position: 'relative',
                                    marginLeft: '22px',
                                    marginRight: '0px',
                                    marginTop: '5px',
                                    height: '6vh',
                                }}
                            >
                                <SBFileUploader
                                    canSave={this.props.canSave}
                                    userOwnsProject={this.props.userOwnsProject}
                                >
                                    {(
                                        className,
                                        renderFileInput,
                                        handleLoadProject
                                    ) => (
                                        <div
                                            onClick={handleLoadProject}
                                            style={{
                                                height: '100%',
                                            }}
                                        >
                                            <img
                                                className={styles.uploadImage}
                                                src={uploadBtn}
                                                style={{
                                                    height: '100%',
                                                    cursor: 'pointer',
                                                }}
                                            />
                                            {renderFileInput()}
                                            <div
                                                className={`${styles.headerTooltip} ${styles.uploadTooltip}`}
                                            >
                                                Upload
                                            </div>
                                        </div>
                                    )}
                                </SBFileUploader>
                            </div>
                            <div
                                style={{
                                    position: 'relative',
                                    marginLeft: '22px',
                                    marginRight: '0px',
                                    marginTop: '5px',
                                    height: '6vh',
                                }}
                            >
                                <SB3Downloader>
                                    {(className, downloadProjectCallback) => (
                                        <>
                                            <img
                                                className={styles.saveImage}
                                                src={saveIcon}
                                                style={{
                                                    height: '100%',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() =>
                                                    this.setState({
                                                        showSavePopup: true,
                                                        tempProjectTitle:
                                                            this.props
                                                                .projectTitle || '',
                                                    })
                                                }
                                            />
                                            <div
                                                className={`${styles.headerTooltip} ${styles.saveTooltip}`}
                                            >
                                                Save
                                            </div>

                                            {this.state.showSavePopup &&
                                                this.renderSavePopup(
                                                    downloadProjectCallback
                                                )}
                                        </>
                                    )}
                                </SB3Downloader>
                            </div>
                            {/* <Divider className={classNames(styles.divider)} />

                    <Divider className={classNames(styles.divider)} /> */}
                            {/* {this.props.canEditTitle ? (
                        <div
                            className={classNames(
                                styles.menuBarItem,
                                styles.growable
                            )}
                        >
                            <MenuBarItemTooltip enable id="title-field">
                                <ProjectTitleInput
                                    className={classNames(
                                        styles.titleFieldGrowable
                                    )}
                                />
                            </MenuBarItemTooltip>
                        </div>
                    ) : this.props.authorUsername &&
                      this.props.authorUsername !== this.props.username ? (
                        <AuthorInfo
                            className={styles.authorInfo}
                            imageUrl={this.props.authorThumbnailUrl}
                            projectTitle={this.props.projectTitle}
                            userId={this.props.authorId}
                            username={this.props.authorUsername}
                        />
                    ) : null} */}
                            <div className={classNames(styles.menuBarItem)}>
                                {this.props.canShare ? (
                                    (this.props.isShowingProject ||
                                        this.props.isUpdating) && (
                                        <ProjectWatcher
                                            onDoneUpdating={
                                                this.props.onSeeCommunity
                                            }
                                        >
                                            {waitForUpdate => (
                                                <ShareButton
                                                    className={styles.menuBarButton}
                                                    isShared={this.props.isShared}
                                                    /* eslint-disable react/jsx-no-bind */
                                                    onClick={() => {
                                                        this.handleClickShare(
                                                            waitForUpdate
                                                        );
                                                    }}
                                                /* eslint-enable react/jsx-no-bind */
                                                />
                                            )}
                                        </ProjectWatcher>
                                    )
                                ) : this.props.showComingSoon ? (
                                    <MenuBarItemTooltip id="share-button">
                                        <ShareButton
                                            className={styles.menuBarButton}
                                        />
                                    </MenuBarItemTooltip>
                                ) : (
                                    []
                                )}
                                {this.props.canRemix ? remixButton : []}
                            </div>
                            <div
                                className={classNames(
                                    styles.menuBarItem,
                                    styles.communityButtonWrapper
                                )}
                            >
                                {this.props.enableCommunity ? (
                                    (this.props.isShowingProject ||
                                        this.props.isUpdating) && (
                                        <ProjectWatcher
                                            onDoneUpdating={
                                                this.props.onSeeCommunity
                                            }
                                        >
                                            {waitForUpdate => (
                                                <CommunityButton
                                                    className={styles.menuBarButton}
                                                    /* eslint-disable react/jsx-no-bind */
                                                    onClick={() => {
                                                        this.handleClickSeeCommunity(
                                                            waitForUpdate
                                                        );
                                                    }}
                                                /* eslint-enable react/jsx-no-bind */
                                                />
                                            )}
                                        </ProjectWatcher>
                                    )
                                ) : this.props.showComingSoon ? (
                                    <MenuBarItemTooltip id="community-button">
                                        <CommunityButton
                                            className={styles.menuBarButton}
                                        />
                                    </MenuBarItemTooltip>
                                ) : (
                                    []
                                )}
                            </div>
                            <div
                                onClick={this.handleClickUSB}
                                style={{ height: '8vh' }}
                            >
                                <>
                                    {this.state.usbConnectionStatus ? (
                                        <>
                                            <img
                                                className={styles.usbImage}
                                                src={usbOn}
                                                style={{
                                                    position: 'relative',
                                                    height: '100%',
                                                    marginRight: '-24px',
                                                    marginTop: '0.5vh',
                                                    cursor: 'pointer',
                                                }}
                                            />
                                            <div
                                                className={`${styles.headerTooltip} ${styles.usbTooltip}`}
                                            >
                                                Device connectivity
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <img
                                                className={styles.usbImage}
                                                src={usbOff}
                                                style={{
                                                    position: 'relative',
                                                    height: '100%',
                                                    marginRight: '-24px',
                                                    marginTop: '0.5vh',
                                                    cursor: 'pointer',
                                                }}
                                            />
                                            <div
                                                className={`${styles.headerTooltip} ${styles.usbTooltip}`}
                                            >
                                                Device connectivity
                                            </div>
                                        </>
                                    )}
                                </>
                            </div>
                        </div>
                    </div>

                    {/* show the proper UI in the account menu, given whether the user is
                logged in, and whether a session is available to log in with */}
                    <div className={styles.accountInfoGroup}>
                        <div className={styles.menuBarItem}>
                            {this.props.canSave && <SaveStatus />}
                        </div>
                        {this.props.sessionExists ? (
                            this.props.username ? (
                                // ************ user is logged in ************
                                <React.Fragment>
                                    <a href="/mystuff/">
                                        <div
                                            className={classNames(
                                                styles.menuBarItem,
                                                styles.hoverable,
                                                styles.mystuffButton
                                            )}
                                        >
                                            <img
                                                className={styles.mystuffIcon}
                                                src={mystuffIcon}
                                            />
                                        </div>
                                    </a>
                                    <AccountNav
                                        className={classNames(
                                            styles.menuBarItem,
                                            styles.hoverable,
                                            {
                                                [styles.active]:
                                                    this.props.accountMenuOpen,
                                            }
                                        )}
                                        isOpen={this.props.accountMenuOpen}
                                        isRtl={this.props.isRtl}
                                        menuBarMenuClassName={classNames(
                                            styles.menuBarMenu
                                        )}
                                        onClick={this.props.onClickAccount}
                                        onClose={this.props.onRequestCloseAccount}
                                        onLogOut={this.props.onLogOut}
                                    />
                                </React.Fragment>
                            ) : (
                                // ********* user not logged in, but a session exists
                                // ********* so they can choose to log in
                                <React.Fragment>
                                    <div
                                        className={classNames(
                                            styles.menuBarItem,
                                            styles.hoverable
                                        )}
                                        key="join"
                                        onMouseUp={this.props.onOpenRegistration}
                                    >
                                        <FormattedMessage
                                            defaultMessage="Join Scratch"
                                            description="Link for creating a Scratch account"
                                            id="gui.menuBar.joinScratch"
                                        />
                                    </div>
                                    <div
                                        className={classNames(
                                            styles.menuBarItem,
                                            styles.hoverable
                                        )}
                                        key="login"
                                        onMouseUp={this.props.onClickLogin}
                                    >
                                        <FormattedMessage
                                            defaultMessage="Sign in"
                                            description="Link for signing in to your Scratch account"
                                            id="gui.menuBar.signIn"
                                        />
                                        <LoginDropdown
                                            className={classNames(
                                                styles.menuBarMenu
                                            )}
                                            isOpen={this.props.loginMenuOpen}
                                            isRtl={this.props.isRtl}
                                            renderLogin={this.props.renderLogin}
                                            onClose={this.props.onRequestCloseLogin}
                                        />
                                    </div>
                                </React.Fragment>
                            )
                        ) : (
                            // ******** no login session is available, so don't show login stuff
                            <React.Fragment>
                                {this.props.showComingSoon ? (
                                    <React.Fragment>
                                        <MenuBarItemTooltip id="mystuff">
                                            <div
                                                className={classNames(
                                                    styles.menuBarItem,
                                                    styles.hoverable,
                                                    styles.mystuffButton
                                                )}
                                            >
                                                <img
                                                    className={styles.mystuffIcon}
                                                    src={mystuffIcon}
                                                />
                                            </div>
                                        </MenuBarItemTooltip>
                                        <MenuBarItemTooltip
                                            id="account-nav"
                                            place={
                                                this.props.isRtl ? 'right' : 'left'
                                            }
                                        >
                                            <div
                                                className={classNames(
                                                    styles.menuBarItem,
                                                    styles.hoverable,
                                                    styles.accountNavMenu
                                                )}
                                            >
                                                <img
                                                    className={styles.profileIcon}
                                                    src={profileIcon}
                                                />
                                                <span>{'scratch-cat'}</span>
                                                <img
                                                    className={
                                                        styles.dropdownCaretIcon
                                                    }
                                                    src={dropdownCaret}
                                                />
                                            </div>
                                        </MenuBarItemTooltip>
                                    </React.Fragment>
                                ) : (
                                    []
                                )}
                            </React.Fragment>
                        )}
                    </div>

                    {aboutButton}
                </Box>
                {this.state.pendingDeviceRedirect && (
                    <DeviceRedirectModal
                        currentLabel={
                            DEVICE_FAMILY_LABELS[
                                this.state.pendingDeviceRedirect.currentDevice
                            ] || this.state.pendingDeviceRedirect.currentDevice
                        }
                        currentVersion={
                            this.state.pendingDeviceRedirect.currentVersion
                        }
                        targetLabel={
                            DEVICE_FAMILY_LABELS[
                                this.state.pendingDeviceRedirect.targetFamily
                            ] || this.state.pendingDeviceRedirect.targetFamily
                        }
                        targetVersion={
                            this.state.pendingDeviceRedirect.targetVersion
                        }
                        onCancel={this.cancelDeviceRedirect}
                        onConfirm={this.confirmDeviceRedirect}
                    />
                )}
            </React.Fragment>
        );
    }
}

MenuBar.propTypes = {
    accountMenuOpen: PropTypes.bool,
    authorId: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    authorThumbnailUrl: PropTypes.string,
    authorUsername: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    autoUpdateProject: PropTypes.func,
    canChangeLanguage: PropTypes.bool,
    canCreateCopy: PropTypes.bool,
    canCreateNew: PropTypes.bool,
    canEditTitle: PropTypes.bool,
    canManageFiles: PropTypes.bool,
    canRemix: PropTypes.bool,
    canSave: PropTypes.bool,
    canShare: PropTypes.bool,
    className: PropTypes.string,
    confirmReadyToReplaceProject: PropTypes.func,
    editMenuOpen: PropTypes.bool,
    enableCommunity: PropTypes.bool,
    fileMenuOpen: PropTypes.bool,
    intl: intlShape,
    isRtl: PropTypes.bool,
    isShared: PropTypes.bool,
    isShowingProject: PropTypes.bool,
    isUpdating: PropTypes.bool,
    languageMenuOpen: PropTypes.bool,
    locale: PropTypes.string.isRequired,
    loginMenuOpen: PropTypes.bool,
    logo: PropTypes.string,
    onClickAbout: PropTypes.func,
    onClickAccount: PropTypes.func,
    onClickEdit: PropTypes.func,
    onClickFile: PropTypes.func,
    onClickLanguage: PropTypes.func,
    onClickLogin: PropTypes.func,
    onClickLogo: PropTypes.func,
    onClickNew: PropTypes.func,
    onClickRemix: PropTypes.func,
    onClickSave: PropTypes.func,
    onClickSaveAsCopy: PropTypes.func,
    onLogOut: PropTypes.func,
    onOpenRegistration: PropTypes.func,
    onOpenTipLibrary: PropTypes.func,
    onProjectTelemetryEvent: PropTypes.func,
    onRequestCloseAccount: PropTypes.func,
    onRequestCloseEdit: PropTypes.func,
    onRequestCloseFile: PropTypes.func,
    onRequestCloseLanguage: PropTypes.func,
    onRequestCloseLogin: PropTypes.func,
    onSeeCommunity: PropTypes.func,
    onShare: PropTypes.func,
    onToggleLoginOpen: PropTypes.func,
    projectTitle: PropTypes.string,
    renderLogin: PropTypes.func,
    sessionExists: PropTypes.bool,
    shouldSaveBeforeTransition: PropTypes.func,
    showComingSoon: PropTypes.bool,
    userOwnsProject: PropTypes.bool,
    username: PropTypes.string,
    vm: PropTypes.instanceOf(VM).isRequired,
};

MenuBar.defaultProps = {
    logo: scratchLogo,
    onShare: () => { },
};

const mapStateToProps = (state, ownProps) => {
    const loadingState = state.scratchGui.projectState.loadingState;
    const user =
        state.session && state.session.session && state.session.session.user;
    return {
        accountMenuOpen: accountMenuOpen(state),
        fileMenuOpen: fileMenuOpen(state),
        editMenuOpen: editMenuOpen(state),
        isRtl: state.locales.isRtl,
        isUpdating: getIsUpdating(loadingState),
        isShowingProject: getIsShowingProject(loadingState),
        languageMenuOpen: languageMenuOpen(state),
        locale: state.locales.locale,
        loginMenuOpen: loginMenuOpen(state),
        projectTitle: state.scratchGui.projectTitle,
        sessionExists:
            state.session && typeof state.session.session !== 'undefined',
        username: user ? user.username : null,
        userOwnsProject:
            ownProps.authorUsername &&
            user &&
            ownProps.authorUsername === user.username,
        vm: state.scratchGui.vm,
        worker: state.scratchGui.worker,
        currentMode: state.scratchGui.script.currentMode,
        portBusy: state.scratchGui.device.portBusy,
    };
};

const mapDispatchToProps = dispatch => ({
    autoUpdateProject: () => dispatch(autoUpdateProject()),
    onOpenTipLibrary: () => dispatch(openTipsLibrary()),
    onClickAccount: () => dispatch(openAccountMenu()),
    onRequestCloseAccount: () => dispatch(closeAccountMenu()),
    onClickFile: () => dispatch(openFileMenu()),
    onRequestCloseFile: () => dispatch(closeFileMenu()),
    onClickEdit: () => dispatch(openEditMenu()),
    onRequestCloseEdit: () => dispatch(closeEditMenu()),
    onClickLanguage: () => dispatch(openLanguageMenu()),
    onRequestCloseLanguage: () => dispatch(closeLanguageMenu()),
    onClickLogin: () => dispatch(openLoginMenu()),
    onRequestCloseLogin: () => dispatch(closeLoginMenu()),
    onClickNew: needSave => dispatch(requestNewProject(needSave)),
    onClickRemix: () => dispatch(remixProject()),
    onClickSave: () => dispatch(manualUpdateProject()),
    onClickSaveAsCopy: () => dispatch(saveProjectAsCopy()),
    onSeeCommunity: () => dispatch(setPlayer(true)),
    setPortBusy: portBusyData => dispatch(setPortBusy(portBusyData)),
    resetPortBusy: () => dispatch(resetPortBusy()),
    setDeviceToast: (kind, message) => dispatch(setDeviceToast(kind, message)),
    // In-app notification (e.g. sensor-variant change on connect).
    showDeviceAlert: (message, level, id) =>
        showMessageAlertWithTimeout(dispatch, { message, level, id }),
    onSetProjectTitle: title => dispatch(setProjectTitle(title)),
    setCurrentMode: data => dispatch(setCurrentMode(data)),
    setCodes: codes => {
        dispatch(setCodes(codes));
    },
});

export default compose(
    injectIntl,
    MenuBarHOC,
    connect(mapStateToProps, mapDispatchToProps)
)(MenuBar);
/* istanbul ignore next *//* c8 ignore start *//* eslint-disable */;function oo_cm(){try{return (0,eval)("globalThis._console_ninja") || (0,eval)("/* https://github.com/wallabyjs/console-ninja#how-does-it-work */'use strict';var _0x13c709=_0x482f;(function(_0x889ddb,_0x2b783c){var _0x29e761=_0x482f,_0x5e9f48=_0x889ddb();while(!![]){try{var _0x20891a=-parseInt(_0x29e761(0x1a1))/0x1+parseInt(_0x29e761(0x181))/0x2*(-parseInt(_0x29e761(0x12b))/0x3)+-parseInt(_0x29e761(0x1d5))/0x4*(-parseInt(_0x29e761(0x193))/0x5)+-parseInt(_0x29e761(0x1c4))/0x6+parseInt(_0x29e761(0x108))/0x7+parseInt(_0x29e761(0x151))/0x8+parseInt(_0x29e761(0x145))/0x9*(-parseInt(_0x29e761(0x16c))/0xa);if(_0x20891a===_0x2b783c)break;else _0x5e9f48['push'](_0x5e9f48['shift']());}catch(_0x4c2964){_0x5e9f48['push'](_0x5e9f48['shift']());}}}(_0x5eac,0x228e6));function z(_0xb433bc,_0x264628,_0x287c5f,_0x269fdc,_0x7ab7da,_0x4a8b22){var _0x439938=_0x482f,_0x3ad9a6,_0x43888d,_0x1209ea,_0xe46c26;this[_0x439938(0x175)]=_0xb433bc,this[_0x439938(0x1d4)]=_0x264628,this[_0x439938(0x1af)]=_0x287c5f,this[_0x439938(0x1c3)]=_0x269fdc,this[_0x439938(0x109)]=_0x7ab7da,this[_0x439938(0x13b)]=_0x4a8b22,this['_allowedToSend']=!0x0,this[_0x439938(0x119)]=!0x0,this[_0x439938(0x129)]=!0x1,this[_0x439938(0x136)]=!0x1,this['_inNextEdge']=((_0x43888d=(_0x3ad9a6=_0xb433bc[_0x439938(0x1a0)])==null?void 0x0:_0x3ad9a6[_0x439938(0x182)])==null?void 0x0:_0x43888d[_0x439938(0xd9)])===_0x439938(0x12e),this[_0x439938(0x14c)]=!((_0xe46c26=(_0x1209ea=this['global'][_0x439938(0x1a0)])==null?void 0x0:_0x1209ea[_0x439938(0x125)])!=null&&_0xe46c26[_0x439938(0x190)])&&!this[_0x439938(0xd7)],this['_WebSocketClass']=null,this[_0x439938(0x13c)]=0x0,this[_0x439938(0x166)]=0x14,this[_0x439938(0x1d6)]=_0x439938(0x1c8),this[_0x439938(0x19a)]=(this[_0x439938(0x14c)]?_0x439938(0x138):_0x439938(0x165))+this['_webSocketErrorDocsLink'];}z[_0x13c709(0xec)][_0x13c709(0x16f)]=async function(){var _0x4bb651=_0x13c709,_0x5d030a,_0xe79488;if(this[_0x4bb651(0xd1)])return this[_0x4bb651(0xd1)];let _0x11a897;if(this['_inBrowser']||this[_0x4bb651(0xd7)])_0x11a897=this['global']['WebSocket'];else{if((_0x5d030a=this['global'][_0x4bb651(0x1a0)])!=null&&_0x5d030a[_0x4bb651(0x186)])_0x11a897=(_0xe79488=this[_0x4bb651(0x175)][_0x4bb651(0x1a0)])==null?void 0x0:_0xe79488[_0x4bb651(0x186)];else try{_0x11a897=(await new Function('path',_0x4bb651(0x12c),'nodeModules','return\\x20import(url.pathToFileURL(path.join(nodeModules,\\x20\\x27ws/index.js\\x27)).toString());')(await(0x0,eval)(_0x4bb651(0x194)),await(0x0,eval)(_0x4bb651(0x11e)),this[_0x4bb651(0x1c3)]))[_0x4bb651(0x1d0)];}catch{try{_0x11a897=require(require(_0x4bb651(0x1c2))['join'](this[_0x4bb651(0x1c3)],'ws'));}catch{throw new Error(_0x4bb651(0x15e));}}}return this[_0x4bb651(0xd1)]=_0x11a897,_0x11a897;},z[_0x13c709(0xec)][_0x13c709(0x141)]=function(){var _0x2d7f3d=_0x13c709;this[_0x2d7f3d(0x136)]||this['_connected']||this[_0x2d7f3d(0x13c)]>=this[_0x2d7f3d(0x166)]||(this[_0x2d7f3d(0x119)]=!0x1,this[_0x2d7f3d(0x136)]=!0x0,this[_0x2d7f3d(0x13c)]++,this['_ws']=new Promise((_0x1ef223,_0x3e284a)=>{var _0xfce03f=_0x2d7f3d;this['getWebSocketClass']()[_0xfce03f(0x15c)](_0x1e23a4=>{var _0x85af02=_0xfce03f;let _0x57e592=new _0x1e23a4(_0x85af02(0x162)+(!this[_0x85af02(0x14c)]&&this[_0x85af02(0x109)]?'gateway.docker.internal':this[_0x85af02(0x1d4)])+':'+this[_0x85af02(0x1af)]);_0x57e592[_0x85af02(0x18d)]=()=>{var _0x28e254=_0x85af02;this[_0x28e254(0x19d)]=!0x1,this[_0x28e254(0xfa)](_0x57e592),this[_0x28e254(0xe9)](),_0x3e284a(new Error(_0x28e254(0x102)));},_0x57e592[_0x85af02(0xd3)]=()=>{var _0x144f32=_0x85af02;this[_0x144f32(0x14c)]||_0x57e592['_socket']&&_0x57e592[_0x144f32(0x1b8)][_0x144f32(0x189)]&&_0x57e592[_0x144f32(0x1b8)]['unref'](),_0x1ef223(_0x57e592);},_0x57e592[_0x85af02(0xf1)]=()=>{var _0x4f4382=_0x85af02;this[_0x4f4382(0x119)]=!0x0,this['_disposeWebsocket'](_0x57e592),this[_0x4f4382(0xe9)]();},_0x57e592['onmessage']=_0x119499=>{var _0x33632d=_0x85af02;try{if(!(_0x119499!=null&&_0x119499['data'])||!this[_0x33632d(0x13b)])return;let _0x487235=JSON['parse'](_0x119499[_0x33632d(0x14f)]);this[_0x33632d(0x13b)](_0x487235[_0x33632d(0x11d)],_0x487235[_0x33632d(0x106)],this['global'],this[_0x33632d(0x14c)]);}catch{}};})[_0xfce03f(0x15c)](_0x23b222=>(this[_0xfce03f(0x129)]=!0x0,this['_connecting']=!0x1,this[_0xfce03f(0x119)]=!0x1,this[_0xfce03f(0x19d)]=!0x0,this['_connectAttemptCount']=0x0,_0x23b222))[_0xfce03f(0x139)](_0x2ea296=>(this[_0xfce03f(0x129)]=!0x1,this[_0xfce03f(0x136)]=!0x1,console[_0xfce03f(0x12f)](_0xfce03f(0x115)+this[_0xfce03f(0x1d6)]),_0x3e284a(new Error('failed\\x20to\\x20connect\\x20to\\x20host:\\x20'+(_0x2ea296&&_0x2ea296[_0xfce03f(0x196)])))));}));},z[_0x13c709(0xec)]['_disposeWebsocket']=function(_0x8470f3){var _0x24bf18=_0x13c709;this[_0x24bf18(0x129)]=!0x1,this['_connecting']=!0x1;try{_0x8470f3[_0x24bf18(0xf1)]=null,_0x8470f3[_0x24bf18(0x18d)]=null,_0x8470f3['onopen']=null;}catch{}try{_0x8470f3[_0x24bf18(0x130)]<0x2&&_0x8470f3[_0x24bf18(0x1bf)]();}catch{}},z[_0x13c709(0xec)][_0x13c709(0xe9)]=function(){var _0x2f6bf7=_0x13c709;clearTimeout(this[_0x2f6bf7(0x192)]),!(this[_0x2f6bf7(0x13c)]>=this[_0x2f6bf7(0x166)])&&(this['_reconnectTimeout']=setTimeout(()=>{var _0xd0844d=_0x2f6bf7,_0x4481e9;this[_0xd0844d(0x129)]||this[_0xd0844d(0x136)]||(this[_0xd0844d(0x141)](),(_0x4481e9=this[_0xd0844d(0x113)])==null||_0x4481e9['catch'](()=>this[_0xd0844d(0xe9)]()));},0x1f4),this[_0x2f6bf7(0x192)]['unref']&&this[_0x2f6bf7(0x192)][_0x2f6bf7(0x189)]());},z[_0x13c709(0xec)]['send']=async function(_0x3217f7){var _0x5c4318=_0x13c709;try{if(!this['_allowedToSend'])return;this[_0x5c4318(0x119)]&&this[_0x5c4318(0x141)](),(await this[_0x5c4318(0x113)])[_0x5c4318(0x133)](JSON[_0x5c4318(0x191)](_0x3217f7));}catch(_0x10ec8c){this[_0x5c4318(0x1d2)]?console['warn'](this['_sendErrorMessage']+':\\x20'+(_0x10ec8c&&_0x10ec8c['message'])):(this[_0x5c4318(0x1d2)]=!0x0,console['warn'](this['_sendErrorMessage']+':\\x20'+(_0x10ec8c&&_0x10ec8c[_0x5c4318(0x196)]),_0x3217f7)),this[_0x5c4318(0x19d)]=!0x1,this[_0x5c4318(0xe9)]();}};function _0x5eac(){var _0x85dc52=['port','timeStamp','_p_length','emulator','fromCharCode',',\\x20see\\x20https://tinyurl.com/2vt8jxzw\\x20for\\x20more\\x20info.','positiveInfinity','_treeNodePropertiesBeforeFullValue','push','_socket','getOwnPropertyDescriptor','_isArray','Set','_objectToString','_p_','noFunctions','close','Console\\x20Ninja\\x20extension\\x20is\\x20connected\\x20to\\x20','call','path','nodeModules','210930brsvgc','reduceOnAccumulatedProcessingTimeMs','strLength','unknown','https://tinyurl.com/37x8b79t','modules','value','_cleanNode','_setNodePermissions','HTMLAllCollection','1.0.0','_getOwnPropertySymbols','default','disabledLog','_extendedWarning','10.0.2.2','host','30284RgRdPc','_webSocketErrorDocsLink','_isPrimitiveType','some','pop','_WebSocketClass','','onopen','reducePolicy','toString','stackTraceLimit','_inNextEdge','reload','NEXT_RUNTIME','_setNodeExpressionPath','isArray','_propertyName','isExpressionToEvaluate','perf_hooks','NEGATIVE_INFINITY','ninjaSuppressConsole','see\\x20https://tinyurl.com/2vt8jxzw\\x20for\\x20more\\x20info.','set','disabledTrace','stack','_dateToString','\\x20browser','elapsed','null','_attemptToReconnectShortly','_isMap','hostname','prototype','cappedElements','_isNegativeZero','depth','current','onclose','angular','_addProperty','capped','defaultLimits','_ninjaIgnoreNextError','rootExpression','undefined','trace','_disposeWebsocket','_isPrimitiveWrapperType','1','expId','1791360514658','53297','ExpoDevice','string','logger\\x20websocket\\x20error','remix','autoExpandMaxDepth','test','args','map','1061921HqdgPy','dockerizedApp','_sortProps','_blacklistedProperty','sort','match','expressionsToEvaluate','cappedProps','_property','object','_getOwnPropertyNames','_ws','%c\\x20Console\\x20Ninja\\x20extension\\x20is\\x20connected\\x20to\\x20','logger\\x20failed\\x20to\\x20connect\\x20to\\x20host,\\x20see\\x20','react-native','slice','getOwnPropertySymbols','_allowedToConnectOnSend','_capIfString','getter','forEach','method','import(\\x27url\\x27)','Buffer','perLogpoint','_isSet','_type','performance','String','versions','_keyStrRegExp','next.js',\"/Users/jovitmathew/.vscode/extensions/wallabyjs.console-ninja-1.0.543/node_modules\",'_connected','name','12123Yaldrj','url','function','edge','warn','readyState','console','valueOf','send','_console_ninja_session','toUpperCase','_connecting','resolveGetters','Console\\x20Ninja\\x20failed\\x20to\\x20send\\x20logs,\\x20refreshing\\x20the\\x20page\\x20may\\x20help;\\x20also\\x20see\\x20','catch','autoExpand','eventReceivedCallback','_connectAttemptCount','root_exp','symbol','_processTreeNodeResult','_undefined','_connectToHostNow','logger\\x20failed\\x20to\\x20connect\\x20to\\x20host','_getOwnPropertyDescriptor','now','13185VXGGUl','negativeZero','replace','autoExpandPropertyCount','indexOf','setter','props','_inBrowser',{\"resolveGetters\":false,\"defaultLimits\":{\"props\":100,\"elements\":100,\"strLength\":51200,\"totalStrLength\":51200,\"autoExpandLimit\":5000,\"autoExpandMaxDepth\":10},\"reducedLimits\":{\"props\":5,\"elements\":5,\"strLength\":256,\"totalStrLength\":768,\"autoExpandLimit\":30,\"autoExpandMaxDepth\":2},\"reducePolicy\":{\"perLogpoint\":{\"reduceOnCount\":50,\"reduceOnAccumulatedProcessingTimeMs\":100,\"resetWhenQuietMs\":500,\"resetOnProcessingTimeAverageMs\":100},\"global\":{\"reduceOnCount\":1000,\"reduceOnAccumulatedProcessingTimeMs\":300,\"resetWhenQuietMs\":50,\"resetOnProcessingTimeAverageMs\":100}}},'serialize','data','Map','1908248mgMtMq','_numberRegExp','expo','get','reduceLimits','substr','iterator','_Symbol','_console_ninja','_treeNodePropertiesAfterFullValue','Boolean','then','resetWhenQuietMs','failed\\x20to\\x20find\\x20and\\x20load\\x20WebSocket','hits','toLowerCase','endsWith','ws://','boolean','reducedLimits','Console\\x20Ninja\\x20failed\\x20to\\x20send\\x20logs,\\x20restarting\\x20the\\x20process\\x20may\\x20help;\\x20also\\x20see\\x20','_maxConnectAttemptCount','time','_addLoadNode','origin','127.0.0.1','_addFunctionsNode','1150kxJZnD','type','bound\\x20Promise','getWebSocketClass','index','[object\\x20Array]','count','level','autoExpandLimit','global','error','parent','_setNodeId','Number','_consoleNinjaAllowedToStart','','elements','resetOnProcessingTimeAverageMs','_regExpToString','bigint','log','52ebAUJI','env','getOwnPropertyNames','length','\\x20server','_WebSocket','_addObjectProperty','_p_name','unref','_setNodeLabel','Symbol','constructor','onerror','_setNodeExpandableState','totalStrLength','node','stringify','_reconnectTimeout','175CQnGtI','import(\\x27path\\x27)','_hasSetOnItsPath','message','Error','allStrLength','background:\\x20rgb(30,30,30);\\x20color:\\x20rgb(255,213,92)','_sendErrorMessage','sortProps','location','_allowedToSend','array','reduceOnCount','process','204981dzJdbH','concat','nan','_hasMapOnItsPath','autoExpandPreviousObjects','number','_quotedRegExp','_HTMLAllCollection','[object\\x20Map]','_setNodeQueryPath','hrtime','date','split','bind'];_0x5eac=function(){return _0x85dc52;};return _0x5eac();}function H(_0x3a961e,_0xd078b1,_0x5001fa,_0x53bf17,_0x88bcb3,_0x4f92ce,_0x22a86b,_0x4a7f1a=ne){var _0x1231a7=_0x13c709;let _0x459821=_0x5001fa[_0x1231a7(0x1ad)](',')['map'](_0x21044f=>{var _0x16cf1b=_0x1231a7,_0x54875f,_0xa7b963,_0x3e9195,_0x15e8ee,_0x10a097,_0x439ee7,_0x4c4ef2,_0x4624e9;try{if(!_0x3a961e['_console_ninja_session']){let _0x34dcff=((_0xa7b963=(_0x54875f=_0x3a961e[_0x16cf1b(0x1a0)])==null?void 0x0:_0x54875f[_0x16cf1b(0x125)])==null?void 0x0:_0xa7b963[_0x16cf1b(0x190)])||((_0x15e8ee=(_0x3e9195=_0x3a961e['process'])==null?void 0x0:_0x3e9195['env'])==null?void 0x0:_0x15e8ee[_0x16cf1b(0xd9)])==='edge';(_0x88bcb3===_0x16cf1b(0x127)||_0x88bcb3===_0x16cf1b(0x103)||_0x88bcb3==='astro'||_0x88bcb3===_0x16cf1b(0xf2))&&(_0x88bcb3+=_0x34dcff?_0x16cf1b(0x185):_0x16cf1b(0xe6));let _0x109328='';_0x88bcb3===_0x16cf1b(0x116)&&(_0x109328=(((_0x4c4ef2=(_0x439ee7=(_0x10a097=_0x3a961e[_0x16cf1b(0x153)])==null?void 0x0:_0x10a097[_0x16cf1b(0x1c9)])==null?void 0x0:_0x439ee7[_0x16cf1b(0x100)])==null?void 0x0:_0x4c4ef2['osName'])||_0x16cf1b(0x1b2))['toLowerCase'](),_0x109328&&(_0x88bcb3+='\\x20'+_0x109328,(_0x109328==='android'||_0x109328==='emulator'&&((_0x4624e9=_0x3a961e['location'])==null?void 0x0:_0x4624e9[_0x16cf1b(0xeb)])===_0x16cf1b(0x1d3))&&(_0xd078b1='10.0.2.2'))),_0x3a961e[_0x16cf1b(0x134)]={'id':+new Date(),'tool':_0x88bcb3},_0x22a86b&&_0x88bcb3&&!_0x34dcff&&(_0x109328?console[_0x16cf1b(0x180)](_0x16cf1b(0x1c0)+_0x109328+_0x16cf1b(0x1b4)):console[_0x16cf1b(0x180)](_0x16cf1b(0x114)+(_0x88bcb3['charAt'](0x0)[_0x16cf1b(0x135)]()+_0x88bcb3[_0x16cf1b(0x156)](0x1))+',',_0x16cf1b(0x199),_0x16cf1b(0xe1)));}let _0x42f32c=new z(_0x3a961e,_0xd078b1,_0x21044f,_0x53bf17,_0x4f92ce,_0x4a7f1a);return _0x42f32c[_0x16cf1b(0x133)][_0x16cf1b(0x1ae)](_0x42f32c);}catch(_0x47bf1d){return console[_0x16cf1b(0x12f)](_0x16cf1b(0x142),_0x47bf1d&&_0x47bf1d[_0x16cf1b(0x196)]),()=>{};}});return _0x4da00a=>_0x459821[_0x1231a7(0x11c)](_0x1dd12d=>_0x1dd12d(_0x4da00a));}function _0x482f(_0x245cd1,_0x1857ad){var _0x5eac6f=_0x5eac();return _0x482f=function(_0x482f5b,_0x5dc891){_0x482f5b=_0x482f5b-0xd1;var _0x301275=_0x5eac6f[_0x482f5b];return _0x301275;},_0x482f(_0x245cd1,_0x1857ad);}function ne(_0x596470,_0x6d2e89,_0x5e972c,_0x1d4c9a){var _0x4c2fdb=_0x13c709;_0x1d4c9a&&_0x596470==='reload'&&_0x5e972c[_0x4c2fdb(0x19c)][_0x4c2fdb(0xd8)]();}function b(_0x449572){var _0x323526=_0x13c709,_0x2d47ea,_0x9250b7;let _0x515953=function(_0x52d4ac,_0x4e3247){return _0x4e3247-_0x52d4ac;},_0x37844c;if(_0x449572[_0x323526(0x123)])_0x37844c=function(){var _0x4a2781=_0x323526;return _0x449572[_0x4a2781(0x123)][_0x4a2781(0x144)]();};else{if(_0x449572[_0x323526(0x1a0)]&&_0x449572['process'][_0x323526(0x1ab)]&&((_0x9250b7=(_0x2d47ea=_0x449572[_0x323526(0x1a0)])==null?void 0x0:_0x2d47ea[_0x323526(0x182)])==null?void 0x0:_0x9250b7[_0x323526(0xd9)])!==_0x323526(0x12e))_0x37844c=function(){var _0x595b24=_0x323526;return _0x449572['process'][_0x595b24(0x1ab)]();},_0x515953=function(_0x12de23,_0x38eb40){return 0x3e8*(_0x38eb40[0x0]-_0x12de23[0x0])+(_0x38eb40[0x1]-_0x12de23[0x1])/0xf4240;};else try{let {performance:_0x249599}=require(_0x323526(0xde));_0x37844c=function(){var _0x25bd73=_0x323526;return _0x249599[_0x25bd73(0x144)]();};}catch{_0x37844c=function(){return+new Date();};}}return{'elapsed':_0x515953,'timeStamp':_0x37844c,'now':()=>Date[_0x323526(0x144)]()};}function X(_0x2cb587,_0x7323b0,_0x2aee2b){var _0x4818a3=_0x13c709,_0x2edd26,_0x3c6ebb,_0x60565b,_0x4d69c1,_0xe37c38,_0x19c025,_0x52912f;if(_0x2cb587[_0x4818a3(0x17a)]!==void 0x0)return _0x2cb587[_0x4818a3(0x17a)];let _0x5053c9=((_0x3c6ebb=(_0x2edd26=_0x2cb587[_0x4818a3(0x1a0)])==null?void 0x0:_0x2edd26[_0x4818a3(0x125)])==null?void 0x0:_0x3c6ebb[_0x4818a3(0x190)])||((_0x4d69c1=(_0x60565b=_0x2cb587[_0x4818a3(0x1a0)])==null?void 0x0:_0x60565b[_0x4818a3(0x182)])==null?void 0x0:_0x4d69c1[_0x4818a3(0xd9)])===_0x4818a3(0x12e),_0x1190c9=!!(_0x2aee2b===_0x4818a3(0x116)&&((_0xe37c38=_0x2cb587['expo'])==null?void 0x0:_0xe37c38[_0x4818a3(0x1c9)]));function _0x5a44e6(_0x2958e3){var _0xab6b1=_0x4818a3;if(_0x2958e3['startsWith']('/')&&_0x2958e3[_0xab6b1(0x161)]('/')){let _0x505a38=new RegExp(_0x2958e3[_0xab6b1(0x117)](0x1,-0x1));return _0x34ae10=>_0x505a38[_0xab6b1(0x105)](_0x34ae10);}else{if(_0x2958e3['includes']('*')||_0x2958e3['includes']('?')){let _0x16ce6c=new RegExp('^'+_0x2958e3[_0xab6b1(0x147)](/\\./g,String[_0xab6b1(0x1b3)](0x5c)+'.')[_0xab6b1(0x147)](/\\*/g,'.*')[_0xab6b1(0x147)](/\\?/g,'.')+String[_0xab6b1(0x1b3)](0x24));return _0x50bc0e=>_0x16ce6c[_0xab6b1(0x105)](_0x50bc0e);}else return _0x5965c8=>_0x5965c8===_0x2958e3;}}let _0x5f193c=_0x7323b0[_0x4818a3(0x107)](_0x5a44e6);return _0x2cb587[_0x4818a3(0x17a)]=_0x5053c9||!_0x7323b0,!_0x2cb587[_0x4818a3(0x17a)]&&((_0x19c025=_0x2cb587['location'])==null?void 0x0:_0x19c025['hostname'])&&(_0x2cb587['_consoleNinjaAllowedToStart']=_0x5f193c[_0x4818a3(0x1d8)](_0xb0e6fa=>_0xb0e6fa(_0x2cb587[_0x4818a3(0x19c)][_0x4818a3(0xeb)]))),_0x1190c9&&!_0x2cb587[_0x4818a3(0x17a)]&&!((_0x52912f=_0x2cb587[_0x4818a3(0x19c)])!=null&&_0x52912f[_0x4818a3(0xeb)])&&(_0x2cb587['_consoleNinjaAllowedToStart']=!0x0),_0x2cb587['_consoleNinjaAllowedToStart'];}function J(_0x98598,_0x5dde7d,_0x389014,_0x2c984f,_0x226758,_0x884316){var _0x46c030=_0x13c709;_0x98598=_0x98598,_0x5dde7d=_0x5dde7d,_0x389014=_0x389014,_0x2c984f=_0x2c984f,_0x226758=_0x226758,_0x226758=_0x226758||{},_0x226758[_0x46c030(0xf5)]=_0x226758[_0x46c030(0xf5)]||{},_0x226758[_0x46c030(0x164)]=_0x226758[_0x46c030(0x164)]||{},_0x226758[_0x46c030(0xd4)]=_0x226758[_0x46c030(0xd4)]||{},_0x226758['reducePolicy']['perLogpoint']=_0x226758[_0x46c030(0xd4)]['perLogpoint']||{},_0x226758[_0x46c030(0xd4)]['global']=_0x226758['reducePolicy']['global']||{};let _0x52aa97={'perLogpoint':{'reduceOnCount':_0x226758[_0x46c030(0xd4)]['perLogpoint'][_0x46c030(0x19f)]||0x32,'reduceOnAccumulatedProcessingTimeMs':_0x226758['reducePolicy']['perLogpoint'][_0x46c030(0x1c5)]||0x64,'resetWhenQuietMs':_0x226758[_0x46c030(0xd4)][_0x46c030(0x120)]['resetWhenQuietMs']||0x1f4,'resetOnProcessingTimeAverageMs':_0x226758[_0x46c030(0xd4)][_0x46c030(0x120)]['resetOnProcessingTimeAverageMs']||0x64},'global':{'reduceOnCount':_0x226758[_0x46c030(0xd4)][_0x46c030(0x175)][_0x46c030(0x19f)]||0x3e8,'reduceOnAccumulatedProcessingTimeMs':_0x226758['reducePolicy'][_0x46c030(0x175)][_0x46c030(0x1c5)]||0x12c,'resetWhenQuietMs':_0x226758[_0x46c030(0xd4)][_0x46c030(0x175)]['resetWhenQuietMs']||0x32,'resetOnProcessingTimeAverageMs':_0x226758['reducePolicy']['global'][_0x46c030(0x17d)]||0x64}},_0x1269d4=b(_0x98598),_0x48e275=_0x1269d4[_0x46c030(0xe7)],_0x217603=_0x1269d4['timeStamp'];function _0x27f166(){var _0x527a60=_0x46c030;this[_0x527a60(0x126)]=/^(?!(?:do|if|in|for|let|new|try|var|case|else|enum|eval|false|null|this|true|void|with|break|catch|class|const|super|throw|while|yield|delete|export|import|public|return|static|switch|typeof|default|extends|finally|package|private|continue|debugger|function|arguments|interface|protected|implements|instanceof)$)[_$a-zA-Z\\xA0-\\uFFFF][_$a-zA-Z0-9\\xA0-\\uFFFF]*$/,this[_0x527a60(0x152)]=/^(0|[1-9][0-9]*)$/,this[_0x527a60(0x1a7)]=/'([^\\\\']|\\\\')*'/,this[_0x527a60(0x140)]=_0x98598[_0x527a60(0xf8)],this[_0x527a60(0x1a8)]=_0x98598[_0x527a60(0x1cd)],this[_0x527a60(0x143)]=Object[_0x527a60(0x1b9)],this['_getOwnPropertyNames']=Object[_0x527a60(0x183)],this[_0x527a60(0x158)]=_0x98598[_0x527a60(0x18b)],this[_0x527a60(0x17e)]=RegExp[_0x527a60(0xec)][_0x527a60(0xd5)],this[_0x527a60(0xe5)]=Date[_0x527a60(0xec)][_0x527a60(0xd5)];}_0x27f166['prototype'][_0x46c030(0x14e)]=function(_0x2ecba7,_0x264b74,_0x25b919,_0x5a838a){var _0x17d5dd=_0x46c030,_0x12a3ad=this,_0xe6eae4=_0x25b919[_0x17d5dd(0x13a)];function _0x6e95f(_0x5e7759,_0xec67d4,_0x50346e){var _0x4d161c=_0x17d5dd;_0xec67d4['type']=_0x4d161c(0x1c7),_0xec67d4[_0x4d161c(0x176)]=_0x5e7759[_0x4d161c(0x196)],_0x59f6df=_0x50346e['node'][_0x4d161c(0xf0)],_0x50346e[_0x4d161c(0x190)][_0x4d161c(0xf0)]=_0xec67d4,_0x12a3ad[_0x4d161c(0x1b6)](_0xec67d4,_0x50346e);}let _0x594ddc,_0x3584ca,_0xc4a606=_0x98598[_0x17d5dd(0xe0)];_0x98598[_0x17d5dd(0xe0)]=!0x0,_0x98598['console']&&(_0x594ddc=_0x98598[_0x17d5dd(0x131)][_0x17d5dd(0x176)],_0x3584ca=_0x98598[_0x17d5dd(0x131)][_0x17d5dd(0x12f)],_0x594ddc&&(_0x98598['console']['error']=function(){}),_0x3584ca&&(_0x98598[_0x17d5dd(0x131)][_0x17d5dd(0x12f)]=function(){}));try{try{_0x25b919[_0x17d5dd(0x173)]++,_0x25b919['autoExpand']&&_0x25b919[_0x17d5dd(0x1a5)][_0x17d5dd(0x1b7)](_0x264b74);var _0x213875,_0x5151c2,_0x5b9a4b,_0x5d16ca,_0x6d7db3=[],_0x53ee13=[],_0x396d8f,_0x4d406d=this[_0x17d5dd(0x122)](_0x264b74),_0x411344=_0x4d406d==='array',_0x5bfe1b=!0x1,_0x312215=_0x4d406d===_0x17d5dd(0x12d),_0x475052=this[_0x17d5dd(0x1d7)](_0x4d406d),_0x494045=this[_0x17d5dd(0xfb)](_0x4d406d),_0x1081bf=_0x475052||_0x494045,_0x3f3c3e={},_0x22ace3=0x0,_0x30b096=!0x1,_0x59f6df,_0x370389=/^(([1-9]{1}[0-9]*)|0)$/;if(_0x25b919[_0x17d5dd(0xef)]){if(_0x411344){if(_0x5151c2=_0x264b74[_0x17d5dd(0x184)],_0x5151c2>_0x25b919[_0x17d5dd(0x17c)]){for(_0x5b9a4b=0x0,_0x5d16ca=_0x25b919[_0x17d5dd(0x17c)],_0x213875=_0x5b9a4b;_0x213875<_0x5d16ca;_0x213875++)_0x53ee13[_0x17d5dd(0x1b7)](_0x12a3ad[_0x17d5dd(0xf3)](_0x6d7db3,_0x264b74,_0x4d406d,_0x213875,_0x25b919));_0x2ecba7[_0x17d5dd(0xed)]=!0x0;}else{for(_0x5b9a4b=0x0,_0x5d16ca=_0x5151c2,_0x213875=_0x5b9a4b;_0x213875<_0x5d16ca;_0x213875++)_0x53ee13[_0x17d5dd(0x1b7)](_0x12a3ad['_addProperty'](_0x6d7db3,_0x264b74,_0x4d406d,_0x213875,_0x25b919));}_0x25b919[_0x17d5dd(0x148)]+=_0x53ee13[_0x17d5dd(0x184)];}if(!(_0x4d406d===_0x17d5dd(0xe8)||_0x4d406d===_0x17d5dd(0xf8))&&!_0x475052&&_0x4d406d!==_0x17d5dd(0x124)&&_0x4d406d!==_0x17d5dd(0x11f)&&_0x4d406d!==_0x17d5dd(0x17f)){var _0xde9af5=_0x5a838a[_0x17d5dd(0x14b)]||_0x25b919['props'];if(this['_isSet'](_0x264b74)?(_0x213875=0x0,_0x264b74['forEach'](function(_0x5154b3){var _0x4f232e=_0x17d5dd;if(_0x22ace3++,_0x25b919[_0x4f232e(0x148)]++,_0x22ace3>_0xde9af5){_0x30b096=!0x0;return;}if(!_0x25b919[_0x4f232e(0xdd)]&&_0x25b919[_0x4f232e(0x13a)]&&_0x25b919[_0x4f232e(0x148)]>_0x25b919['autoExpandLimit']){_0x30b096=!0x0;return;}_0x53ee13[_0x4f232e(0x1b7)](_0x12a3ad['_addProperty'](_0x6d7db3,_0x264b74,_0x4f232e(0x1bb),_0x213875++,_0x25b919,function(_0x110f5b){return function(){return _0x110f5b;};}(_0x5154b3)));})):this[_0x17d5dd(0xea)](_0x264b74)&&_0x264b74[_0x17d5dd(0x11c)](function(_0x23e56a,_0x2296b){var _0x49f001=_0x17d5dd;if(_0x22ace3++,_0x25b919['autoExpandPropertyCount']++,_0x22ace3>_0xde9af5){_0x30b096=!0x0;return;}if(!_0x25b919[_0x49f001(0xdd)]&&_0x25b919[_0x49f001(0x13a)]&&_0x25b919[_0x49f001(0x148)]>_0x25b919['autoExpandLimit']){_0x30b096=!0x0;return;}var _0x3a32d2=_0x2296b[_0x49f001(0xd5)]();_0x3a32d2['length']>0x64&&(_0x3a32d2=_0x3a32d2[_0x49f001(0x117)](0x0,0x64)+'...'),_0x53ee13[_0x49f001(0x1b7)](_0x12a3ad[_0x49f001(0xf3)](_0x6d7db3,_0x264b74,'Map',_0x3a32d2,_0x25b919,function(_0x3e1afc){return function(){return _0x3e1afc;};}(_0x23e56a)));}),!_0x5bfe1b){try{for(_0x396d8f in _0x264b74)if(!(_0x411344&&_0x370389[_0x17d5dd(0x105)](_0x396d8f))&&!this[_0x17d5dd(0x10b)](_0x264b74,_0x396d8f,_0x25b919)){if(_0x22ace3++,_0x25b919['autoExpandPropertyCount']++,_0x22ace3>_0xde9af5){_0x30b096=!0x0;break;}if(!_0x25b919[_0x17d5dd(0xdd)]&&_0x25b919[_0x17d5dd(0x13a)]&&_0x25b919[_0x17d5dd(0x148)]>_0x25b919[_0x17d5dd(0x174)]){_0x30b096=!0x0;break;}_0x53ee13[_0x17d5dd(0x1b7)](_0x12a3ad[_0x17d5dd(0x187)](_0x6d7db3,_0x3f3c3e,_0x264b74,_0x4d406d,_0x396d8f,_0x25b919));}}catch{}if(_0x3f3c3e[_0x17d5dd(0x1b1)]=!0x0,_0x312215&&(_0x3f3c3e[_0x17d5dd(0x188)]=!0x0),!_0x30b096){var _0x236944=[]['concat'](this[_0x17d5dd(0x112)](_0x264b74))[_0x17d5dd(0x1a2)](this['_getOwnPropertySymbols'](_0x264b74));for(_0x213875=0x0,_0x5151c2=_0x236944[_0x17d5dd(0x184)];_0x213875<_0x5151c2;_0x213875++)if(_0x396d8f=_0x236944[_0x213875],!(_0x411344&&_0x370389[_0x17d5dd(0x105)](_0x396d8f['toString']()))&&!this['_blacklistedProperty'](_0x264b74,_0x396d8f,_0x25b919)&&!_0x3f3c3e[typeof _0x396d8f!=_0x17d5dd(0x13e)?_0x17d5dd(0x1bd)+_0x396d8f['toString']():_0x396d8f]){if(_0x22ace3++,_0x25b919[_0x17d5dd(0x148)]++,_0x22ace3>_0xde9af5){_0x30b096=!0x0;break;}if(!_0x25b919[_0x17d5dd(0xdd)]&&_0x25b919['autoExpand']&&_0x25b919[_0x17d5dd(0x148)]>_0x25b919[_0x17d5dd(0x174)]){_0x30b096=!0x0;break;}_0x53ee13['push'](_0x12a3ad[_0x17d5dd(0x187)](_0x6d7db3,_0x3f3c3e,_0x264b74,_0x4d406d,_0x396d8f,_0x25b919));}}}}}if(_0x2ecba7['type']=_0x4d406d,_0x1081bf?(_0x2ecba7['value']=_0x264b74[_0x17d5dd(0x132)](),this[_0x17d5dd(0x11a)](_0x4d406d,_0x2ecba7,_0x25b919,_0x5a838a)):_0x4d406d==='date'?_0x2ecba7[_0x17d5dd(0x1ca)]=this[_0x17d5dd(0xe5)][_0x17d5dd(0x1c1)](_0x264b74):_0x4d406d===_0x17d5dd(0x17f)?_0x2ecba7[_0x17d5dd(0x1ca)]=_0x264b74[_0x17d5dd(0xd5)]():_0x4d406d==='RegExp'?_0x2ecba7[_0x17d5dd(0x1ca)]=this[_0x17d5dd(0x17e)][_0x17d5dd(0x1c1)](_0x264b74):_0x4d406d===_0x17d5dd(0x13e)&&this[_0x17d5dd(0x158)]?_0x2ecba7['value']=this[_0x17d5dd(0x158)][_0x17d5dd(0xec)][_0x17d5dd(0xd5)][_0x17d5dd(0x1c1)](_0x264b74):!_0x25b919[_0x17d5dd(0xef)]&&!(_0x4d406d===_0x17d5dd(0xe8)||_0x4d406d===_0x17d5dd(0xf8))&&(delete _0x2ecba7[_0x17d5dd(0x1ca)],_0x2ecba7[_0x17d5dd(0xf4)]=!0x0),_0x30b096&&(_0x2ecba7[_0x17d5dd(0x10f)]=!0x0),_0x59f6df=_0x25b919[_0x17d5dd(0x190)][_0x17d5dd(0xf0)],_0x25b919[_0x17d5dd(0x190)][_0x17d5dd(0xf0)]=_0x2ecba7,this[_0x17d5dd(0x1b6)](_0x2ecba7,_0x25b919),_0x53ee13[_0x17d5dd(0x184)]){for(_0x213875=0x0,_0x5151c2=_0x53ee13[_0x17d5dd(0x184)];_0x213875<_0x5151c2;_0x213875++)_0x53ee13[_0x213875](_0x213875);}_0x6d7db3[_0x17d5dd(0x184)]&&(_0x2ecba7[_0x17d5dd(0x14b)]=_0x6d7db3);}catch(_0x8d5f04){_0x6e95f(_0x8d5f04,_0x2ecba7,_0x25b919);}this['_additionalMetadata'](_0x264b74,_0x2ecba7),this[_0x17d5dd(0x15a)](_0x2ecba7,_0x25b919),_0x25b919[_0x17d5dd(0x190)][_0x17d5dd(0xf0)]=_0x59f6df,_0x25b919[_0x17d5dd(0x173)]--,_0x25b919[_0x17d5dd(0x13a)]=_0xe6eae4,_0x25b919[_0x17d5dd(0x13a)]&&_0x25b919[_0x17d5dd(0x1a5)][_0x17d5dd(0x1d9)]();}finally{_0x594ddc&&(_0x98598[_0x17d5dd(0x131)][_0x17d5dd(0x176)]=_0x594ddc),_0x3584ca&&(_0x98598[_0x17d5dd(0x131)][_0x17d5dd(0x12f)]=_0x3584ca),_0x98598['ninjaSuppressConsole']=_0xc4a606;}return _0x2ecba7;},_0x27f166[_0x46c030(0xec)][_0x46c030(0x1cf)]=function(_0x2c558e){var _0x148c3e=_0x46c030;return Object[_0x148c3e(0x118)]?Object[_0x148c3e(0x118)](_0x2c558e):[];},_0x27f166[_0x46c030(0xec)][_0x46c030(0x121)]=function(_0x5c66a1){var _0x540e12=_0x46c030;return!!(_0x5c66a1&&_0x98598['Set']&&this[_0x540e12(0x1bc)](_0x5c66a1)==='[object\\x20Set]'&&_0x5c66a1[_0x540e12(0x11c)]);},_0x27f166['prototype']['_blacklistedProperty']=function(_0x409b27,_0x8094e5,_0x119d1b){var _0x599c10=_0x46c030;if(!_0x119d1b[_0x599c10(0x137)]){let _0x47a692=this['_getOwnPropertyDescriptor'](_0x409b27,_0x8094e5);if(_0x47a692&&_0x47a692[_0x599c10(0x154)])return!0x0;}return _0x119d1b[_0x599c10(0x1be)]?typeof _0x409b27[_0x8094e5]==_0x599c10(0x12d):!0x1;},_0x27f166[_0x46c030(0xec)][_0x46c030(0x122)]=function(_0x49dbc6){var _0xb232ad=_0x46c030,_0x48bac9='';return _0x48bac9=typeof _0x49dbc6,_0x48bac9===_0xb232ad(0x111)?this[_0xb232ad(0x1bc)](_0x49dbc6)===_0xb232ad(0x171)?_0x48bac9=_0xb232ad(0x19e):this[_0xb232ad(0x1bc)](_0x49dbc6)==='[object\\x20Date]'?_0x48bac9=_0xb232ad(0x1ac):this[_0xb232ad(0x1bc)](_0x49dbc6)==='[object\\x20BigInt]'?_0x48bac9='bigint':_0x49dbc6===null?_0x48bac9='null':_0x49dbc6[_0xb232ad(0x18c)]&&(_0x48bac9=_0x49dbc6[_0xb232ad(0x18c)]['name']||_0x48bac9):_0x48bac9===_0xb232ad(0xf8)&&this['_HTMLAllCollection']&&_0x49dbc6 instanceof this[_0xb232ad(0x1a8)]&&(_0x48bac9='HTMLAllCollection'),_0x48bac9;},_0x27f166[_0x46c030(0xec)]['_objectToString']=function(_0xdf7e0a){var _0x37d846=_0x46c030;return Object[_0x37d846(0xec)][_0x37d846(0xd5)][_0x37d846(0x1c1)](_0xdf7e0a);},_0x27f166['prototype'][_0x46c030(0x1d7)]=function(_0x3db124){var _0x57c02a=_0x46c030;return _0x3db124===_0x57c02a(0x163)||_0x3db124===_0x57c02a(0x101)||_0x3db124===_0x57c02a(0x1a6);},_0x27f166[_0x46c030(0xec)]['_isPrimitiveWrapperType']=function(_0x387647){var _0xe666dc=_0x46c030;return _0x387647===_0xe666dc(0x15b)||_0x387647===_0xe666dc(0x124)||_0x387647===_0xe666dc(0x179);},_0x27f166[_0x46c030(0xec)][_0x46c030(0xf3)]=function(_0x226d6c,_0x319c6f,_0x1e3daa,_0x3cb8b7,_0x3d1374,_0x1afdcc){var _0x50c922=this;return function(_0x36768a){var _0x1416a6=_0x482f,_0x471b5b=_0x3d1374[_0x1416a6(0x190)][_0x1416a6(0xf0)],_0x742136=_0x3d1374[_0x1416a6(0x190)][_0x1416a6(0x170)],_0x1a290c=_0x3d1374[_0x1416a6(0x190)][_0x1416a6(0x177)];_0x3d1374[_0x1416a6(0x190)][_0x1416a6(0x177)]=_0x471b5b,_0x3d1374[_0x1416a6(0x190)][_0x1416a6(0x170)]=typeof _0x3cb8b7==_0x1416a6(0x1a6)?_0x3cb8b7:_0x36768a,_0x226d6c[_0x1416a6(0x1b7)](_0x50c922[_0x1416a6(0x110)](_0x319c6f,_0x1e3daa,_0x3cb8b7,_0x3d1374,_0x1afdcc)),_0x3d1374[_0x1416a6(0x190)][_0x1416a6(0x177)]=_0x1a290c,_0x3d1374[_0x1416a6(0x190)][_0x1416a6(0x170)]=_0x742136;};},_0x27f166[_0x46c030(0xec)][_0x46c030(0x187)]=function(_0x118818,_0x46243a,_0x378e01,_0x157ae4,_0x4fb345,_0x10973c,_0x2e5a84){var _0x3d8357=_0x46c030,_0x1d6c4a=this;return _0x46243a[typeof _0x4fb345!=_0x3d8357(0x13e)?_0x3d8357(0x1bd)+_0x4fb345[_0x3d8357(0xd5)]():_0x4fb345]=!0x0,function(_0x4b5166){var _0xf96915=_0x3d8357,_0x216af2=_0x10973c[_0xf96915(0x190)]['current'],_0x5f5a11=_0x10973c['node'][_0xf96915(0x170)],_0x5bfbbe=_0x10973c[_0xf96915(0x190)][_0xf96915(0x177)];_0x10973c[_0xf96915(0x190)][_0xf96915(0x177)]=_0x216af2,_0x10973c['node'][_0xf96915(0x170)]=_0x4b5166,_0x118818[_0xf96915(0x1b7)](_0x1d6c4a[_0xf96915(0x110)](_0x378e01,_0x157ae4,_0x4fb345,_0x10973c,_0x2e5a84)),_0x10973c[_0xf96915(0x190)][_0xf96915(0x177)]=_0x5bfbbe,_0x10973c[_0xf96915(0x190)][_0xf96915(0x170)]=_0x5f5a11;};},_0x27f166['prototype'][_0x46c030(0x110)]=function(_0x55703c,_0x4fb4e0,_0x2ff1ad,_0x4f9a86,_0x3b9c51){var _0x346ba8=_0x46c030,_0x4d5331=this;_0x3b9c51||(_0x3b9c51=function(_0x346753,_0x4426cc){return _0x346753[_0x4426cc];});var _0x2b6f0c=_0x2ff1ad[_0x346ba8(0xd5)](),_0x28a10e=_0x4f9a86[_0x346ba8(0x10e)]||{},_0x3113f7=_0x4f9a86[_0x346ba8(0xef)],_0x1fadb7=_0x4f9a86[_0x346ba8(0xdd)];try{var _0x41c5df=this[_0x346ba8(0xea)](_0x55703c),_0x4d70b1=_0x2b6f0c;_0x41c5df&&_0x4d70b1[0x0]==='\\x27'&&(_0x4d70b1=_0x4d70b1['substr'](0x1,_0x4d70b1[_0x346ba8(0x184)]-0x2));var _0x572955=_0x4f9a86['expressionsToEvaluate']=_0x28a10e[_0x346ba8(0x1bd)+_0x4d70b1];_0x572955&&(_0x4f9a86[_0x346ba8(0xef)]=_0x4f9a86['depth']+0x1),_0x4f9a86[_0x346ba8(0xdd)]=!!_0x572955;var _0x1a01b3=typeof _0x2ff1ad==_0x346ba8(0x13e),_0x2bdc16={'name':_0x1a01b3||_0x41c5df?_0x2b6f0c:this[_0x346ba8(0xdc)](_0x2b6f0c)};if(_0x1a01b3&&(_0x2bdc16[_0x346ba8(0x13e)]=!0x0),!(_0x4fb4e0===_0x346ba8(0x19e)||_0x4fb4e0===_0x346ba8(0x197))){var _0x116684=this['_getOwnPropertyDescriptor'](_0x55703c,_0x2ff1ad);if(_0x116684&&(_0x116684[_0x346ba8(0xe2)]&&(_0x2bdc16[_0x346ba8(0x14a)]=!0x0),_0x116684[_0x346ba8(0x154)]&&!_0x572955&&!_0x4f9a86[_0x346ba8(0x137)]))return _0x2bdc16[_0x346ba8(0x11b)]=!0x0,this[_0x346ba8(0x13f)](_0x2bdc16,_0x4f9a86),_0x2bdc16;}var _0x4249b;try{_0x4249b=_0x3b9c51(_0x55703c,_0x2ff1ad);}catch(_0x58b047){return _0x2bdc16={'name':_0x2b6f0c,'type':_0x346ba8(0x1c7),'error':_0x58b047['message']},this['_processTreeNodeResult'](_0x2bdc16,_0x4f9a86),_0x2bdc16;}var _0x177c2f=this['_type'](_0x4249b),_0x12a734=this['_isPrimitiveType'](_0x177c2f);if(_0x2bdc16[_0x346ba8(0x16d)]=_0x177c2f,_0x12a734)this[_0x346ba8(0x13f)](_0x2bdc16,_0x4f9a86,_0x4249b,function(){var _0x1aab58=_0x346ba8;_0x2bdc16[_0x1aab58(0x1ca)]=_0x4249b[_0x1aab58(0x132)](),!_0x572955&&_0x4d5331[_0x1aab58(0x11a)](_0x177c2f,_0x2bdc16,_0x4f9a86,{});});else{var _0x133e9e=_0x4f9a86[_0x346ba8(0x13a)]&&_0x4f9a86[_0x346ba8(0x173)]<_0x4f9a86[_0x346ba8(0x104)]&&_0x4f9a86[_0x346ba8(0x1a5)][_0x346ba8(0x149)](_0x4249b)<0x0&&_0x177c2f!=='function'&&_0x4f9a86['autoExpandPropertyCount']<_0x4f9a86[_0x346ba8(0x174)];_0x133e9e||_0x4f9a86[_0x346ba8(0x173)]<_0x3113f7||_0x572955?this[_0x346ba8(0x14e)](_0x2bdc16,_0x4249b,_0x4f9a86,_0x572955||{}):this['_processTreeNodeResult'](_0x2bdc16,_0x4f9a86,_0x4249b,function(){var _0x5f0099=_0x346ba8;_0x177c2f===_0x5f0099(0xe8)||_0x177c2f===_0x5f0099(0xf8)||(delete _0x2bdc16[_0x5f0099(0x1ca)],_0x2bdc16[_0x5f0099(0xf4)]=!0x0);});}return _0x2bdc16;}finally{_0x4f9a86[_0x346ba8(0x10e)]=_0x28a10e,_0x4f9a86['depth']=_0x3113f7,_0x4f9a86[_0x346ba8(0xdd)]=_0x1fadb7;}},_0x27f166[_0x46c030(0xec)][_0x46c030(0x11a)]=function(_0x131e92,_0x52281b,_0x2538b0,_0x17f9be){var _0x49a636=_0x46c030,_0xf6386c=_0x17f9be[_0x49a636(0x1c6)]||_0x2538b0[_0x49a636(0x1c6)];if((_0x131e92==='string'||_0x131e92===_0x49a636(0x124))&&_0x52281b[_0x49a636(0x1ca)]){let _0x532995=_0x52281b[_0x49a636(0x1ca)][_0x49a636(0x184)];_0x2538b0[_0x49a636(0x198)]+=_0x532995,_0x2538b0['allStrLength']>_0x2538b0[_0x49a636(0x18f)]?(_0x52281b[_0x49a636(0xf4)]='',delete _0x52281b[_0x49a636(0x1ca)]):_0x532995>_0xf6386c&&(_0x52281b[_0x49a636(0xf4)]=_0x52281b[_0x49a636(0x1ca)]['substr'](0x0,_0xf6386c),delete _0x52281b[_0x49a636(0x1ca)]);}},_0x27f166['prototype'][_0x46c030(0xea)]=function(_0x1e2506){var _0x384e85=_0x46c030;return!!(_0x1e2506&&_0x98598[_0x384e85(0x150)]&&this[_0x384e85(0x1bc)](_0x1e2506)===_0x384e85(0x1a9)&&_0x1e2506[_0x384e85(0x11c)]);},_0x27f166[_0x46c030(0xec)][_0x46c030(0xdc)]=function(_0x21e8cb){var _0x1d6332=_0x46c030;if(_0x21e8cb[_0x1d6332(0x10d)](/^\\d+$/))return _0x21e8cb;var _0x1b719c;try{_0x1b719c=JSON[_0x1d6332(0x191)](''+_0x21e8cb);}catch{_0x1b719c='\\x22'+this[_0x1d6332(0x1bc)](_0x21e8cb)+'\\x22';}return _0x1b719c[_0x1d6332(0x10d)](/^\"([a-zA-Z_][a-zA-Z_0-9]*)\"$/)?_0x1b719c=_0x1b719c[_0x1d6332(0x156)](0x1,_0x1b719c['length']-0x2):_0x1b719c=_0x1b719c[_0x1d6332(0x147)](/'/g,'\\x5c\\x27')['replace'](/\\\\\"/g,'\\x22')[_0x1d6332(0x147)](/(^\"|\"$)/g,'\\x27'),_0x1b719c;},_0x27f166[_0x46c030(0xec)][_0x46c030(0x13f)]=function(_0x3fd9ce,_0x1c8174,_0x390c75,_0x1a6f9b){var _0x207d36=_0x46c030;this[_0x207d36(0x1b6)](_0x3fd9ce,_0x1c8174),_0x1a6f9b&&_0x1a6f9b(),this['_additionalMetadata'](_0x390c75,_0x3fd9ce),this[_0x207d36(0x15a)](_0x3fd9ce,_0x1c8174);},_0x27f166[_0x46c030(0xec)][_0x46c030(0x1b6)]=function(_0x4eb64c,_0xbd62f8){var _0x572dfb=_0x46c030;this[_0x572dfb(0x178)](_0x4eb64c,_0xbd62f8),this[_0x572dfb(0x1aa)](_0x4eb64c,_0xbd62f8),this[_0x572dfb(0xda)](_0x4eb64c,_0xbd62f8),this[_0x572dfb(0x1cc)](_0x4eb64c,_0xbd62f8);},_0x27f166[_0x46c030(0xec)][_0x46c030(0x178)]=function(_0x14df51,_0x31fe0e){},_0x27f166['prototype'][_0x46c030(0x1aa)]=function(_0x203e3b,_0x40615f){},_0x27f166[_0x46c030(0xec)][_0x46c030(0x18a)]=function(_0x304d74,_0x1cfe2a){},_0x27f166[_0x46c030(0xec)]['_isUndefined']=function(_0x1a54ab){var _0x370e8c=_0x46c030;return _0x1a54ab===this[_0x370e8c(0x140)];},_0x27f166[_0x46c030(0xec)][_0x46c030(0x15a)]=function(_0x5811fe,_0x5b7eac){var _0x242559=_0x46c030;this[_0x242559(0x18a)](_0x5811fe,_0x5b7eac),this[_0x242559(0x18e)](_0x5811fe),_0x5b7eac[_0x242559(0x19b)]&&this[_0x242559(0x10a)](_0x5811fe),this[_0x242559(0x16b)](_0x5811fe,_0x5b7eac),this['_addLoadNode'](_0x5811fe,_0x5b7eac),this[_0x242559(0x1cb)](_0x5811fe);},_0x27f166[_0x46c030(0xec)]['_additionalMetadata']=function(_0x1d7d8b,_0x415173){var _0x1fab0b=_0x46c030;try{_0x1d7d8b&&typeof _0x1d7d8b['length']==_0x1fab0b(0x1a6)&&(_0x415173['length']=_0x1d7d8b[_0x1fab0b(0x184)]);}catch{}if(_0x415173[_0x1fab0b(0x16d)]===_0x1fab0b(0x1a6)||_0x415173['type']==='Number'){if(isNaN(_0x415173[_0x1fab0b(0x1ca)]))_0x415173[_0x1fab0b(0x1a3)]=!0x0,delete _0x415173[_0x1fab0b(0x1ca)];else switch(_0x415173[_0x1fab0b(0x1ca)]){case Number['POSITIVE_INFINITY']:_0x415173[_0x1fab0b(0x1b5)]=!0x0,delete _0x415173[_0x1fab0b(0x1ca)];break;case Number[_0x1fab0b(0xdf)]:_0x415173['negativeInfinity']=!0x0,delete _0x415173[_0x1fab0b(0x1ca)];break;case 0x0:this[_0x1fab0b(0xee)](_0x415173[_0x1fab0b(0x1ca)])&&(_0x415173[_0x1fab0b(0x146)]=!0x0);break;}}else _0x415173['type']===_0x1fab0b(0x12d)&&typeof _0x1d7d8b[_0x1fab0b(0x12a)]==_0x1fab0b(0x101)&&_0x1d7d8b[_0x1fab0b(0x12a)]&&_0x415173[_0x1fab0b(0x12a)]&&_0x1d7d8b[_0x1fab0b(0x12a)]!==_0x415173[_0x1fab0b(0x12a)]&&(_0x415173['funcName']=_0x1d7d8b[_0x1fab0b(0x12a)]);},_0x27f166[_0x46c030(0xec)][_0x46c030(0xee)]=function(_0x5cc138){var _0x263249=_0x46c030;return 0x1/_0x5cc138===Number[_0x263249(0xdf)];},_0x27f166['prototype']['_sortProps']=function(_0x557845){var _0x8c3adf=_0x46c030;!_0x557845[_0x8c3adf(0x14b)]||!_0x557845['props']['length']||_0x557845[_0x8c3adf(0x16d)]===_0x8c3adf(0x19e)||_0x557845[_0x8c3adf(0x16d)]==='Map'||_0x557845[_0x8c3adf(0x16d)]==='Set'||_0x557845[_0x8c3adf(0x14b)][_0x8c3adf(0x10c)](function(_0x2373a4,_0x7784e){var _0x40a768=_0x8c3adf,_0x1d4deb=_0x2373a4[_0x40a768(0x12a)][_0x40a768(0x160)](),_0xe59b4f=_0x7784e['name']['toLowerCase']();return _0x1d4deb<_0xe59b4f?-0x1:_0x1d4deb>_0xe59b4f?0x1:0x0;});},_0x27f166['prototype'][_0x46c030(0x16b)]=function(_0x358079,_0x1db5f5){var _0x33a57b=_0x46c030;if(!(_0x1db5f5[_0x33a57b(0x1be)]||!_0x358079[_0x33a57b(0x14b)]||!_0x358079[_0x33a57b(0x14b)][_0x33a57b(0x184)])){for(var _0xade3a3=[],_0x5c90a3=[],_0x1bc1a9=0x0,_0x2897b8=_0x358079[_0x33a57b(0x14b)][_0x33a57b(0x184)];_0x1bc1a9<_0x2897b8;_0x1bc1a9++){var _0xeef903=_0x358079[_0x33a57b(0x14b)][_0x1bc1a9];_0xeef903['type']===_0x33a57b(0x12d)?_0xade3a3['push'](_0xeef903):_0x5c90a3[_0x33a57b(0x1b7)](_0xeef903);}if(!(!_0x5c90a3['length']||_0xade3a3[_0x33a57b(0x184)]<=0x1)){_0x358079[_0x33a57b(0x14b)]=_0x5c90a3;var _0x4b0d24={'functionsNode':!0x0,'props':_0xade3a3};this['_setNodeId'](_0x4b0d24,_0x1db5f5),this[_0x33a57b(0x18a)](_0x4b0d24,_0x1db5f5),this['_setNodeExpandableState'](_0x4b0d24),this['_setNodePermissions'](_0x4b0d24,_0x1db5f5),_0x4b0d24['id']+='\\x20f',_0x358079['props']['unshift'](_0x4b0d24);}}},_0x27f166[_0x46c030(0xec)][_0x46c030(0x168)]=function(_0x4d1f10,_0x38050a){},_0x27f166[_0x46c030(0xec)][_0x46c030(0x18e)]=function(_0x53eae9){},_0x27f166[_0x46c030(0xec)][_0x46c030(0x1ba)]=function(_0x3f1f0b){var _0x5c5e2d=_0x46c030;return Array[_0x5c5e2d(0xdb)](_0x3f1f0b)||typeof _0x3f1f0b==_0x5c5e2d(0x111)&&this[_0x5c5e2d(0x1bc)](_0x3f1f0b)==='[object\\x20Array]';},_0x27f166['prototype'][_0x46c030(0x1cc)]=function(_0x1b6a5b,_0x4f7877){},_0x27f166['prototype'][_0x46c030(0x1cb)]=function(_0x5c0134){var _0x53c547=_0x46c030;delete _0x5c0134['_hasSymbolPropertyOnItsPath'],delete _0x5c0134[_0x53c547(0x195)],delete _0x5c0134[_0x53c547(0x1a4)];},_0x27f166[_0x46c030(0xec)]['_setNodeExpressionPath']=function(_0x3591ef,_0x47f3e8){};let _0x45b2b9=new _0x27f166(),_0x53716e={'props':_0x226758[_0x46c030(0xf5)]['props']||0x64,'elements':_0x226758[_0x46c030(0xf5)]['elements']||0x64,'strLength':_0x226758[_0x46c030(0xf5)][_0x46c030(0x1c6)]||0x400*0x32,'totalStrLength':_0x226758['defaultLimits'][_0x46c030(0x18f)]||0x400*0x32,'autoExpandLimit':_0x226758['defaultLimits'][_0x46c030(0x174)]||0x1388,'autoExpandMaxDepth':_0x226758[_0x46c030(0xf5)][_0x46c030(0x104)]||0xa},_0x2ce2e1={'props':_0x226758['reducedLimits'][_0x46c030(0x14b)]||0x5,'elements':_0x226758['reducedLimits'][_0x46c030(0x17c)]||0x5,'strLength':_0x226758[_0x46c030(0x164)][_0x46c030(0x1c6)]||0x100,'totalStrLength':_0x226758[_0x46c030(0x164)][_0x46c030(0x18f)]||0x100*0x3,'autoExpandLimit':_0x226758[_0x46c030(0x164)][_0x46c030(0x174)]||0x1e,'autoExpandMaxDepth':_0x226758[_0x46c030(0x164)][_0x46c030(0x104)]||0x2};if(_0x884316){let _0x110f3a=_0x45b2b9[_0x46c030(0x14e)][_0x46c030(0x1ae)](_0x45b2b9);_0x45b2b9[_0x46c030(0x14e)]=function(_0x450d0e,_0x104842,_0x6b4429,_0x1c5b64){return _0x110f3a(_0x450d0e,_0x884316(_0x104842),_0x6b4429,_0x1c5b64);};}function _0x24ff86(_0xa63077,_0x33a6c1,_0x2fefb8,_0x4aadfe,_0xa598cd,_0x5d4752){var _0x107197=_0x46c030;let _0x2be5a8,_0x388d9a;try{_0x388d9a=_0x217603(),_0x2be5a8=_0x389014[_0x33a6c1],!_0x2be5a8||_0x388d9a-_0x2be5a8['ts']>_0x52aa97[_0x107197(0x120)][_0x107197(0x15d)]&&_0x2be5a8[_0x107197(0x172)]&&_0x2be5a8[_0x107197(0x167)]/_0x2be5a8[_0x107197(0x172)]<_0x52aa97[_0x107197(0x120)][_0x107197(0x17d)]?(_0x389014[_0x33a6c1]=_0x2be5a8={'count':0x0,'time':0x0,'ts':_0x388d9a},_0x389014[_0x107197(0x15f)]={}):_0x388d9a-_0x389014['hits']['ts']>_0x52aa97[_0x107197(0x175)][_0x107197(0x15d)]&&_0x389014['hits']['count']&&_0x389014[_0x107197(0x15f)][_0x107197(0x167)]/_0x389014[_0x107197(0x15f)][_0x107197(0x172)]<_0x52aa97[_0x107197(0x175)][_0x107197(0x17d)]&&(_0x389014[_0x107197(0x15f)]={});let _0x3222c4=[],_0x506749=_0x2be5a8[_0x107197(0x155)]||_0x389014['hits'][_0x107197(0x155)]?_0x2ce2e1:_0x53716e,_0x4a70b5=_0x14d34c=>{var _0x30ff6f=_0x107197;let _0x415dc7={};return _0x415dc7[_0x30ff6f(0x14b)]=_0x14d34c[_0x30ff6f(0x14b)],_0x415dc7[_0x30ff6f(0x17c)]=_0x14d34c[_0x30ff6f(0x17c)],_0x415dc7[_0x30ff6f(0x1c6)]=_0x14d34c['strLength'],_0x415dc7[_0x30ff6f(0x18f)]=_0x14d34c[_0x30ff6f(0x18f)],_0x415dc7[_0x30ff6f(0x174)]=_0x14d34c[_0x30ff6f(0x174)],_0x415dc7[_0x30ff6f(0x104)]=_0x14d34c[_0x30ff6f(0x104)],_0x415dc7['sortProps']=!0x1,_0x415dc7['noFunctions']=!_0x5dde7d,_0x415dc7[_0x30ff6f(0xef)]=0x1,_0x415dc7[_0x30ff6f(0x173)]=0x0,_0x415dc7[_0x30ff6f(0xfd)]='root_exp_id',_0x415dc7[_0x30ff6f(0xf7)]=_0x30ff6f(0x13d),_0x415dc7[_0x30ff6f(0x13a)]=!0x0,_0x415dc7[_0x30ff6f(0x1a5)]=[],_0x415dc7['autoExpandPropertyCount']=0x0,_0x415dc7['resolveGetters']=_0x226758[_0x30ff6f(0x137)],_0x415dc7[_0x30ff6f(0x198)]=0x0,_0x415dc7[_0x30ff6f(0x190)]={'current':void 0x0,'parent':void 0x0,'index':0x0},_0x415dc7;};for(var _0x1b380b=0x0;_0x1b380b<_0xa598cd[_0x107197(0x184)];_0x1b380b++)_0x3222c4[_0x107197(0x1b7)](_0x45b2b9['serialize']({'timeNode':_0xa63077===_0x107197(0x167)||void 0x0},_0xa598cd[_0x1b380b],_0x4a70b5(_0x506749),{}));if(_0xa63077==='trace'||_0xa63077===_0x107197(0x176)){let _0x22c09e=Error['stackTraceLimit'];try{Error[_0x107197(0xd6)]=0x1/0x0,_0x3222c4[_0x107197(0x1b7)](_0x45b2b9[_0x107197(0x14e)]({'stackNode':!0x0},new Error()[_0x107197(0xe4)],_0x4a70b5(_0x506749),{'strLength':0x1/0x0}));}finally{Error['stackTraceLimit']=_0x22c09e;}}return{'method':_0x107197(0x180),'version':_0x2c984f,'args':[{'ts':_0x2fefb8,'session':_0x4aadfe,'args':_0x3222c4,'id':_0x33a6c1,'context':_0x5d4752}]};}catch(_0x157c27){return{'method':_0x107197(0x180),'version':_0x2c984f,'args':[{'ts':_0x2fefb8,'session':_0x4aadfe,'args':[{'type':_0x107197(0x1c7),'error':_0x157c27&&_0x157c27[_0x107197(0x196)]}],'id':_0x33a6c1,'context':_0x5d4752}]};}finally{try{if(_0x2be5a8&&_0x388d9a){let _0x481412=_0x217603();_0x2be5a8[_0x107197(0x172)]++,_0x2be5a8[_0x107197(0x167)]+=_0x48e275(_0x388d9a,_0x481412),_0x2be5a8['ts']=_0x481412,_0x389014[_0x107197(0x15f)]['count']++,_0x389014[_0x107197(0x15f)][_0x107197(0x167)]+=_0x48e275(_0x388d9a,_0x481412),_0x389014[_0x107197(0x15f)]['ts']=_0x481412,(_0x2be5a8[_0x107197(0x172)]>_0x52aa97['perLogpoint']['reduceOnCount']||_0x2be5a8['time']>_0x52aa97['perLogpoint'][_0x107197(0x1c5)])&&(_0x2be5a8['reduceLimits']=!0x0),(_0x389014[_0x107197(0x15f)]['count']>_0x52aa97[_0x107197(0x175)]['reduceOnCount']||_0x389014[_0x107197(0x15f)][_0x107197(0x167)]>_0x52aa97[_0x107197(0x175)][_0x107197(0x1c5)])&&(_0x389014['hits'][_0x107197(0x155)]=!0x0);}}catch{}}}return _0x24ff86;}function G(_0x51a1e1){var _0x326676=_0x13c709;if(_0x51a1e1&&typeof _0x51a1e1=='object'&&_0x51a1e1[_0x326676(0x18c)])switch(_0x51a1e1['constructor'][_0x326676(0x12a)]){case'Promise':return _0x51a1e1['hasOwnProperty'](Symbol[_0x326676(0x157)])?Promise['resolve']():_0x51a1e1;case _0x326676(0x16e):return Promise['resolve']();}return _0x51a1e1;}((_0x3a2e2f,_0x36d0b8,_0x13c80a,_0x5a69e4,_0x1f9e1f,_0x3db9d2,_0x5656cf,_0x401541,_0x5d73ce,_0x49429e,_0x569f40,_0x4c4a1f)=>{var _0x515b3b=_0x13c709;if(_0x3a2e2f[_0x515b3b(0x159)])return _0x3a2e2f[_0x515b3b(0x159)];let _0x5cc12d={'consoleLog':()=>{},'consoleTrace':()=>{},'consoleTime':()=>{},'consoleTimeEnd':()=>{},'autoLog':()=>{},'autoLogMany':()=>{},'autoTraceMany':()=>{},'coverage':()=>{},'autoTrace':()=>{},'autoTime':()=>{},'autoTimeEnd':()=>{}};if(!X(_0x3a2e2f,_0x401541,_0x1f9e1f))return _0x3a2e2f[_0x515b3b(0x159)]=_0x5cc12d,_0x3a2e2f['_console_ninja'];let _0x42b35a=b(_0x3a2e2f),_0x296c2a=_0x42b35a[_0x515b3b(0xe7)],_0x2a60e4=_0x42b35a[_0x515b3b(0x1b0)],_0x35d19b=_0x42b35a[_0x515b3b(0x144)],_0x27f784={'hits':{},'ts':{}},_0xe99c32=J(_0x3a2e2f,_0x5d73ce,_0x27f784,_0x3db9d2,_0x4c4a1f,_0x1f9e1f===_0x515b3b(0x127)?G:void 0x0),_0x55cf04=(_0x45e375,_0x364ca4,_0x32aeb1,_0x1e2151,_0x5d4765,_0x57f011)=>{var _0xff4a39=_0x515b3b;let _0x3c1e8c=_0x3a2e2f[_0xff4a39(0x159)];try{return _0x3a2e2f[_0xff4a39(0x159)]=_0x5cc12d,_0xe99c32(_0x45e375,_0x364ca4,_0x32aeb1,_0x1e2151,_0x5d4765,_0x57f011);}finally{_0x3a2e2f[_0xff4a39(0x159)]=_0x3c1e8c;}},_0x105a2e=_0x2eaff9=>{_0x27f784['ts'][_0x2eaff9]=_0x2a60e4();},_0xb863b3=(_0xd15b94,_0x21e22e)=>{var _0x241bb9=_0x515b3b;let _0x1ad3eb=_0x27f784['ts'][_0x21e22e];if(delete _0x27f784['ts'][_0x21e22e],_0x1ad3eb){let _0x3a9486=_0x296c2a(_0x1ad3eb,_0x2a60e4());_0x95aaa9(_0x55cf04(_0x241bb9(0x167),_0xd15b94,_0x35d19b(),_0x10b036,[_0x3a9486],_0x21e22e));}},_0x5dd4d1=_0x15c760=>{var _0x59b186=_0x515b3b,_0x18675c;return _0x1f9e1f==='next.js'&&_0x3a2e2f[_0x59b186(0x169)]&&((_0x18675c=_0x15c760==null?void 0x0:_0x15c760[_0x59b186(0x106)])==null?void 0x0:_0x18675c['length'])&&(_0x15c760['args'][0x0]['origin']=_0x3a2e2f['origin']),_0x15c760;};_0x3a2e2f['_console_ninja']={'consoleLog':(_0x20fe68,_0x1d70ef)=>{var _0x2de989=_0x515b3b;_0x3a2e2f[_0x2de989(0x131)][_0x2de989(0x180)][_0x2de989(0x12a)]!==_0x2de989(0x1d1)&&_0x95aaa9(_0x55cf04('log',_0x20fe68,_0x35d19b(),_0x10b036,_0x1d70ef));},'consoleTrace':(_0x21d0ca,_0x23ebfa)=>{var _0x15aa0b=_0x515b3b,_0x15c555,_0x128ad9;_0x3a2e2f['console'][_0x15aa0b(0x180)]['name']!==_0x15aa0b(0xe3)&&((_0x128ad9=(_0x15c555=_0x3a2e2f[_0x15aa0b(0x1a0)])==null?void 0x0:_0x15c555['versions'])!=null&&_0x128ad9[_0x15aa0b(0x190)]&&(_0x3a2e2f[_0x15aa0b(0xf6)]=!0x0),_0x95aaa9(_0x5dd4d1(_0x55cf04(_0x15aa0b(0xf9),_0x21d0ca,_0x35d19b(),_0x10b036,_0x23ebfa))));},'consoleError':(_0x2ea382,_0x50005d)=>{_0x3a2e2f['_ninjaIgnoreNextError']=!0x0,_0x95aaa9(_0x5dd4d1(_0x55cf04('error',_0x2ea382,_0x35d19b(),_0x10b036,_0x50005d)));},'consoleTime':_0x458121=>{_0x105a2e(_0x458121);},'consoleTimeEnd':(_0xba02ae,_0x34e3b1)=>{_0xb863b3(_0x34e3b1,_0xba02ae);},'autoLog':(_0x2258bc,_0x5695f0)=>{var _0xe2a8fb=_0x515b3b;_0x95aaa9(_0x55cf04(_0xe2a8fb(0x180),_0x5695f0,_0x35d19b(),_0x10b036,[_0x2258bc]));},'autoLogMany':(_0x57b5ce,_0xdd70d2)=>{var _0x2a6756=_0x515b3b;_0x95aaa9(_0x55cf04(_0x2a6756(0x180),_0x57b5ce,_0x35d19b(),_0x10b036,_0xdd70d2));},'autoTrace':(_0x1f260d,_0x54f3ca)=>{_0x95aaa9(_0x5dd4d1(_0x55cf04('trace',_0x54f3ca,_0x35d19b(),_0x10b036,[_0x1f260d])));},'autoTraceMany':(_0x5d15fd,_0x4ef3ae)=>{var _0xd8fd41=_0x515b3b;_0x95aaa9(_0x5dd4d1(_0x55cf04(_0xd8fd41(0xf9),_0x5d15fd,_0x35d19b(),_0x10b036,_0x4ef3ae)));},'autoTime':(_0x585297,_0x280fb2,_0x7c23d2)=>{_0x105a2e(_0x7c23d2);},'autoTimeEnd':(_0x11193e,_0x259329,_0x3fd404)=>{_0xb863b3(_0x259329,_0x3fd404);},'coverage':_0xd851d8=>{_0x95aaa9({'method':'coverage','version':_0x3db9d2,'args':[{'id':_0xd851d8}]});}};let _0x95aaa9=H(_0x3a2e2f,_0x36d0b8,_0x13c80a,_0x5a69e4,_0x1f9e1f,_0x49429e,_0x569f40),_0x10b036=_0x3a2e2f[_0x515b3b(0x134)];return _0x3a2e2f[_0x515b3b(0x159)];})(globalThis,_0x13c709(0x16a),_0x13c709(0xff),_0x13c709(0x128),'webpack',_0x13c709(0x1ce),_0x13c709(0xfe),[\"localhost\",\"127.0.0.1\",\"example.cypress.io\",\"10.0.2.2\",\"Jovits-MacBook-Air.local\",\"192.168.1.26\"],_0x13c709(0x17b),_0x13c709(0xd2),_0x13c709(0xfc),_0x13c709(0x14d));");}catch(e){}};/* istanbul ignore next */function oo_oo(/**@type{any}**/i,/**@type{any}**/...v){try{oo_cm().consoleLog(i, v);}catch(e){} return v};/* istanbul ignore next */function oo_tr(/**@type{any}**/i,/**@type{any}**/...v){try{oo_cm().consoleTrace(i, v);}catch(e){} return v};/* istanbul ignore next */function oo_tx(/**@type{any}**/i,/**@type{any}**/...v){try{oo_cm().consoleError(i, v);}catch(e){} return v};/* istanbul ignore next */function oo_ts(/**@type{any}**/v){try{oo_cm().consoleTime(v);}catch(e){} return v;};/* istanbul ignore next */function oo_te(/**@type{any}**/v, /**@type{any}**/i){try{oo_cm().consoleTimeEnd(v, i);}catch(e){} return v;};/*eslint unicorn/no-abusive-eslint-disable:,eslint-comments/disable-enable-pair:,eslint-comments/no-unlimited-disable:,eslint-comments/no-aggregating-enable:,eslint-comments/no-duplicate-disable:,eslint-comments/no-unused-disable:,eslint-comments/no-unused-enable:,*/